import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, CircleCheck, Download, FileText, Pencil, Plus, RotateCcw, Save, Send, Stethoscope, Trash2, UserPlus } from 'lucide-react';
import { useUser } from '@/store/useUser';
import { hasAnyPolicy } from '@/utils/policies';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

import { toast } from '@/components/ui/toast';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  UnifiedTable,
  UnifiedTableColumn,
  UnifiedTableDataType,
} from '@/components/ui/unifiedTable/UnifiedTable';
import {
  formatForReadOnlyDisplay,
  formatNewlineCellValue,
  getCellValue,
  getEditorKindForDataType,
  isBooleanDataType,
  isIntegerDataType,
  isLargeTextField,
  isTextLikeDataType,
  mapDbTypeToDataType,
  normalizeForComparison,
  normalizeForSaveValue,
  renderBooleanIcon,
  renderHighlightedMatch,
  shouldPreserveNewlines,
  toBooleanStorageValue,
  toDraftValueForEditor,
  truncateCellValue,
} from './utils/candidateProgramTools';
import type { BooleanStorageFormat } from './utils/candidateProgramTools';
import {
  buildCandidateListQueryVariables,
  getCandidateAddButtonLabel,
  hasActiveCandidateProgramFilters,
  resetCandidateProgramFilters,
  type CandidateProgramFilterSetters,
  type CandidateProgramFilterValues,
} from './utils/candidateProgramFilters';
import { CANDIDATE_PROGRAMS } from './programs';
import { getCandidateProgramFlags } from './config/programFlags';
import { renderProgramControls } from './programs/renderControls';
import {
  ProgressReportFields,
  PROGRESS_REPORT_REQUIRED_FIELDS,
} from './programs/progress-report/ProgressReportFields';
import {
  ProgressReportViewerPicker,
  type ProgressReportViewer,
} from './programs/progress-report/ProgressReportViewerPicker';
import { ProgressReportPreview } from './programs/progress-report/ProgressReportPreview';
import {
  OdrCandidateTaskDialog,
  type OdrCandidateTaskType,
} from './programs/odr/OdrCandidateTaskDialog';
import {
  OdrWorkflowStatusIcon,
  getOdrWorkflowPresentation,
} from './programs/odr/odrWorkflowPresentation';

import { OdrCourtChecklistDialog } from './programs/odr/OdrCourtChecklistDialog';
import { LaiPatientReportDialog } from './programs/lai/LaiPatientReportDialog';
import { PsychBedReportDialog } from './programs/olive/PsychBedReportDialog';
import { IncidentClientsEditor, type IncidentClient } from './programs/inrep/IncidentClientsEditor';
import { downloadIncidentReportPdf } from './programs/inrep/incidentReportPdf';
import { IncidentChoiceField, incidentChoiceFields, IncidentFormOptions } from './programs/inrep/IncidentChoiceField';
import { incidentValidationErrors } from './programs/inrep/incidentValidation';
import { useToolEditingPresence } from './useToolEditingPresence';
import { buildCandidateAllFieldsReportWorkbook, type CandidateAllFieldsReport } from './candidateReportWorkbook';

const INCIDENT_REPORT_EXPORT = gql`query IncidentReportExport($program: String, $startDate: String, $endDate: String) { incidentReportExport(program: $program, startDate: $startDate, endDate: $endDate) { title fields labels rows { cells { key value } } } }`;
const PROGRESS_REPORT_EXPORT = gql`query ProgressReportExport { progressReportExport { title fields labels rows { cells { key value } } } }`;
const INCIDENT_RECORD = gql`query IncidentRecord($id: Int!) { candidateById(component: "inrep", id: $id) { id cells { key value } } }`;
const INCIDENT_FORM_OPTIONS = gql`query IncidentFormOptions { incidentReportFormOptions { reportCategories programs incidentLocations contactedParties organizations housingSites { site program address } } }`;
const incidentClientFields = new Set(['organization_name', 'incident_clients_names', 'incident_clients_cid', 'primary_contact_name', 'primary_contact_phone', 'primary_contact_email']);

const CANDIDATE_SCHEMA = gql`
  query CandidateSchema($component: String!) {
    candidateSchema(component: $component) {
      component
      key
      fields
      fieldMeta {
        name
        label
        help
        writable
        dbType
        maxLength
      }
    }
  }
`;

const CANDIDATE_LIST = gql`
  query CandidateList(
    $component: String!
    $page: Int
    $pageSize: Int
    $search: String
    $searchField: String
    $admitted: String
    $graduated: String
    $dispo: String
    $track: String
    $md: String
    $clinician: String
    $vet: String
    $released: String
    $department: String
    $cancelled: String
    $hub: String
    $odrMhd: String
    $reviewStatus: String
    $workflowProgress: String
    $reviewer: String
    $emailedPd: String
    $addedToDd: String
    $eligible: String
    $homeless: String
    $prePlea: String
    $enrolled: String
    $ih: String
    $imd: String
    $covidGroup: String
    $disposition: String
    $retain: String
    $dateInmateLeft: String
    $jacketReady: String
    $releasePassSent: String
    $currentlyIneligible: String
    $closed: String
    $inrepProgram: String
    $facility: String
    $location: String
    $testType: String
    $clientOrStaff: String
    $program: String
    $testResult: String
    $shot1Status: String
    $shot2Status: String
    $notReceived: String
    $progressIsDraft: String
    $progressCompleted: String
    $progressHub: String
    $progressAppearance: String
    $icmsProvider: String
    $progressCourtDate: String
    $sortField: String
    $sortDirection: String
  ) {
    candidateList(
      component: $component
      page: $page
      pageSize: $pageSize
      search: $search
      searchField: $searchField
      admitted: $admitted
      graduated: $graduated
      dispo: $dispo
      track: $track
      md: $md
      clinician: $clinician
      vet: $vet
      released: $released
      department: $department
      cancelled: $cancelled
      hub: $hub
      odrMhd: $odrMhd
      reviewStatus: $reviewStatus
      workflowProgress: $workflowProgress
      reviewer: $reviewer
      emailedPd: $emailedPd
      addedToDd: $addedToDd
      eligible: $eligible
      homeless: $homeless
      prePlea: $prePlea
      enrolled: $enrolled
      ih: $ih
      imd: $imd
      covidGroup: $covidGroup
      disposition: $disposition
      retain: $retain
      dateInmateLeft: $dateInmateLeft
      jacketReady: $jacketReady
      releasePassSent: $releasePassSent
      currentlyIneligible: $currentlyIneligible
      closed: $closed
      inrepProgram: $inrepProgram
      facility: $facility
      location: $location
      testType: $testType
      clientOrStaff: $clientOrStaff
      program: $program
      testResult: $testResult
      shot1Status: $shot1Status
      shot2Status: $shot2Status
      notReceived: $notReceived
      progressIsDraft: $progressIsDraft
      progressCompleted: $progressCompleted
      progressHub: $progressHub
      progressAppearance: $progressAppearance
      icmsProvider: $icmsProvider
      progressCourtDate: $progressCourtDate
      sortField: $sortField
      sortDirection: $sortDirection
    ) {
      component
      key
      fields
      page
      pageSize
      totalRows
      rows {
        id
        cells {
          key
          value
        }
      }
    }
  }
`;

const UPDATE_CANDIDATE = gql`
  mutation UpdateCandidate($component: String!, $id: Int!, $values: [CandidateInputFieldInput!], $incidentClients: [IncidentReportClientInput!]) {
    updateCandidate(component: $component, id: $id, values: $values, incidentClients: $incidentClients) {
      success
      id
    }
  }
`;

const ODR_CANDIDATE_RECORD = gql`
  query OdrCandidateRecord($id: Int!) {
    candidateById(component: "odr", id: $id) { id cells { key value } }
  }
`;

const REVIEW_ODR_CANDIDATE_CHARGES = gql`
  mutation ReviewOdrCandidateChargesFromRow($candidateId: Int!, $charges: String!) {
    reviewOdrCandidateCharges(candidateId: $candidateId, charges: $charges) {
      success
      id
      value
    }
  }
`;

const PROGRESS_REPORT_VIEWERS = gql`
  query ProgressReportViewers($reportId: Int!) {
    progressReportViewers(reportId: $reportId) { id realname username }
  }
`;

const PROGRESS_REPORT_PARTICIPANT_BY_CID = gql`
  query ProgressReportParticipantByCid($cid: Int, $participantId: Int) {
    progressReportParticipantByCid(cid: $cid, participantId: $participantId) {
      id
      cells { key value }
    }
  }
`;

const SEARCH_PROGRESS_REPORT_PARTICIPANTS = gql`
  query SearchProgressReportParticipants($identifier: String!) {
    searchProgressReportParticipants(identifier: $identifier) {
      participantId
      odrparticipant
      cid
      clientName
      program
    }
  }
`;

const SEARCH_PROGRESS_REPORT_VIEWERS = gql`
  query SearchProgressReportViewers($search: String!) {
    searchProgressReportViewers(search: $search) { id realname username }
  }
`;

const SYNC_PROGRESS_REPORT_VIEWERS = gql`
  mutation SyncProgressReportViewers($reportId: Int!, $viewerIds: [Int!]!) {
    syncProgressReportViewers(reportId: $reportId, viewerIds: $viewerIds) { success id }
  }
`;

const CREATE_CANDIDATE = gql`
  mutation CreateCandidate($component: String!, $values: [CandidateInputFieldInput!], $incidentClients: [IncidentReportClientInput!]) {
    createCandidate(component: $component, values: $values, incidentClients: $incidentClients) {
      success
      id
      field
      value
      notificationQueued
    }
  }
`;

const INCIDENT_CLIENTS = gql`query IncidentReportClients($id: Int!) { incidentReportClients(id: $id) { organization_name incident_clients_names incident_clients_cid primary_contact_name primary_contact_phone primary_contact_email } }`;

const DELETE_CANDIDATE = gql`
  mutation DeleteCandidate($component: String!, $id: Int!) {
    deleteCandidate(component: $component, id: $id) {
      success
      id
    }
  }
`;

const DEFAULT_PAGE_SIZE = 100;
const TABLE_CELL_WRAP_AFTER_CHARS = 32;
const TABLE_CELL_TRUNCATE_AFTER_CHARS = 64;

const confirmProgressReportSubmission = (values: Record<string, string>): boolean => {
  const courtDate = String(values.court_date || '').trim() || 'the scheduled court date';
  const condition = String(values.client_current_condition || '').trim();
  let appearance = String(values.appearance || '').trim();
  if (condition === 'not_well' || condition === 'graduation_encouragement') {
    appearance = 'appearance';
  }
  if (!appearance && condition === 'well') {
    appearance = 'non_appearance';
  }
  if (appearance === 'appearance' || appearance === 'virtual_appearance') {
    return window.confirm(
      `The information within the progress report is accurate as of the date of submission. Both the client and case manager are expected to be present for court by 8:30 am on ${courtDate}. Continue?`,
    );
  }

  return window.confirm(
    `The information in this progress report is accurate as of the date of submission. This matter is scheduled for non-appearance on ${courtDate}; no in-person appearance is required unless otherwise ordered by the Court. Continue?`,
  );
};


type CandidateCell = {
  key: string;
  value: unknown;
};

type CandidateRow = {
  id: number | string;
  cells: CandidateCell[];
};

const ODR_CANDIDATE_TASK_TYPES = new Set<OdrCandidateTaskType>([
  'charges',
  'assign-clinician',
  'clinical-review',
  'affidavit',
  'attorney-email',
  'add-to-dd',
]);

const ODR_WORKFLOW_INTERNAL_FIELDS = new Set([
  'id',
  'emailed_pd_at',
  'emailed_pd_by_user_id',
  'emailed_pd_by_username',
  'added_to_dd_at',
  'added_to_dd_by_user_id',
  'added_to_dd_by_username',
  'charges_reviewed_at',
  'charges_reviewed_by_user_id',
  'charges_reviewed_by_username',
  'clinician_assigned_at',
  'clinician_assigned_by_user_id',
  'clinician_assigned_by_username',
  'clinician_notified_at',
  'clinician_notified_by_user_id',
  'clinician_notified_by_username',
  'participant_created_at',
  'participant_created_by_user_id',
  'participant_created_by_username',
  'lifecycleStage',
  'workflow_progress',
]);

const isOdrAttorneyEmailed = (row: CandidateRow): boolean => {
  const value = getCellValue(row, 'emailed_pd').trim().toLowerCase();
  return value === 'y' || value === 'yes' || value === '1' || value === 'true';
};

const getOdrCandidateRowClassName = (row: CandidateRow): string => {
  const stage = getCellValue(row, 'lifecycleStage') || 'received';
  const status = getOdrWorkflowPresentation(stage);
  const backgroundClassName = isOdrAttorneyEmailed(row)
    ? 'bg-[color:color-mix(in_srgb,var(--semantic-success)_10%,transparent)] hover:bg-[color:color-mix(in_srgb,var(--semantic-success)_10%,transparent)]'
    : 'bg-[var(--semantic-warning-soft)] hover:bg-[var(--semantic-warning-soft)]';

  return cn(
    'border-l-[6px] border-l-[color:var(--odr-workflow-color)]',
    backgroundClassName,
    status.className
  );
};

type CandidateFieldMeta = {
  name: string;
  label?: string;
  writable?: boolean;
  dbType?: string | null;
  maxLength?: number;
};

type ProgressReportParticipantMatch = {
  participantId: number;
  odrparticipant: number | null;
  cid: number | null;
  clientName: string;
  program: string;
};


type MistAddDraft = {
  firstname: string;
  lastname: string;
  facility: string;
  gender: string;
  dob: string;
  n_bk: string;
  court: string;
  n_case: string;
  p_level: string;
  found_ist: string;
  max_date: string;
  progress_report: string;
  disposition_type: string;
  disposition_type_other: string;
  initial_dispo_letter: string;
  conditional_release_outcome: string;
  conditional_release_outcome_other: string;
  conditional_release: string;
  conditional_release_outcome_date: string;
  hearing_date: string;
  date_317_second: string;
  date_317_second_other: string;
  date_date_317_second: string;
  outcome: string;
  outcome_other: string;
  released: string;
  returnee: string;
  team_confidentiality: string;
};

const DEFAULT_MIST_ADD_DRAFT: MistAddDraft = {
  firstname: '',
  lastname: '',
  facility: '',
  gender: '',
  dob: '',
  n_bk: '',
  court: '',
  n_case: '',
  p_level: '',
  found_ist: '',
  max_date: '',
  progress_report: '',
  disposition_type: '',
  disposition_type_other: '',
  initial_dispo_letter: '',
  conditional_release_outcome: '',
  conditional_release_outcome_other: '',
  conditional_release: '',
  conditional_release_outcome_date: '',
  hearing_date: '',
  date_317_second: '',
  date_317_second_other: '',
  date_date_317_second: '',
  outcome: '',
  outcome_other: '',
  released: '',
  returnee: '',
  team_confidentiality: '',
};

type CandidateProgramPageProps = {
  programKey: string;
};

export const CandidateProgramPage = ({ programKey }: CandidateProgramPageProps) => {
  const config = CANDIDATE_PROGRAMS[programKey];
  const { user } = useUser();
  const canManage = programKey !== 'lai' || hasAnyPolicy(user, ['tools:lai:jail~write']);
  const isPromotionPage = ['lai', 'inrep', 'progress-report', 'olive', 'olive-vista'].includes(programKey);
  const writeErrorMessage = (error: unknown, fallback: string, safeMessage: string): string => {
    if (isPromotionPage) return safeMessage;
    return (error as { message?: string } | null)?.message || fallback;
  };
  const incidentProvider = ['provider', 'provider+'].some((group) => user?.groups?.includes(group)) && !['admin', 'superadmin'].some((group) => user?.groups?.includes(group));
  const apolloClient = useApolloClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const incidentAddLinkHandled = useRef(false);
  const incidentAddRequested = programKey === 'inrep' && searchParams.get('action') === 'add';
  const initialSearch = (searchParams.get('search') || '').trim();
  const programFlags = getCandidateProgramFlags(programKey);

  const {
    isMistProgram,
    isOdrProgram,
    isInrepProgram,
    isProgressReportProgram,
    isLeadProgram,
    canOpenParticipantShortcut,
  } = programFlags;
  const taskCandidateId = Number(searchParams.get('candidateId') || 0);
  const taskToken = String(searchParams.get('todo') || '').trim() as OdrCandidateTaskType;
  const selectedOdrTask = ODR_CANDIDATE_TASK_TYPES.has(taskToken) ? taskToken : null;

  const [searchDraft, setSearchDraft] = useState(initialSearch);
  const [search, setSearch] = useState(initialSearch);
  const [searchField, setSearchField] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [cachedTotalPages, setCachedTotalPages] = useState(1);
  const [sortField, setSortField] = useState(programKey === 'lai' ? 'next_due' : 'id');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');

  const [admittedFilter, setAdmittedFilter] = useState<'all' | 'y' | 'n'>('all');
  const [graduatedFilter, setGraduatedFilter] = useState<'y' | 'n'>('n');
  const [mistReleasedFilter, setMistReleasedFilter] = useState<'all' | 'y' | 'n'>('n');
  const [mistReportLoading, setMistReportLoading] = useState<null | 'active' | 'released' | 'all'>(null);
  const [mistFacilityOptions, setMistFacilityOptions] = useState<string[]>([]);
  const [mistAddOpen, setMistAddOpen] = useState(false);
  const [mistAddSubmitting, setMistAddSubmitting] = useState(false);
  const [mistAddDraft, setMistAddDraft] = useState<MistAddDraft>(DEFAULT_MIST_ADD_DRAFT);

  const [dispoFilter, setDispoFilter] = useState<'all' | 'cbr' | 'off_ramp' | 'pc100136' | 'cancelled'>('all');
  const [trackFilter, setTrackFilter] = useState<'all' | 'any' | 'CBR' | 'Off-Ramp' | 'PCU' | '1001.36' | 'Other'>('all');
  const [mdFilter, setMdFilter] = useState<'all' | 'any' | 'none'>('all');
  const [clinicianFilter, setClinicianFilter] = useState<'all' | 'any' | 'none'>('all');
  const [vetFilter, setVetFilter] = useState<'all' | 'Yes' | 'No'>('all');

  const [departmentFilter, setDepartmentFilter] = useState<'all' | 'hub1' | 'hub2' | 'hub3'>('hub1');
  const [dshCancelledFilter, setDshCancelledFilter] = useState<'y' | 'n'>('n');

  const [odrHubFilter, setOdrHubFilter] = useState<'all' | 'hub1' | 'hub2' | 'hub3'>('all');
  const [odrMhdFilter, setOdrMhdFilter] = useState<'all' | 'y' | 'n'>('all');
  const [odrReviewStatusFilter, setOdrReviewStatusFilter] = useState<string>('all');
  const [odrWorkflowFilter, setOdrWorkflowFilter] = useState<string>('all');
  const [odrReviewerFilter, setOdrReviewerFilter] = useState('');
  const [odrEmailedPdFilter, setOdrEmailedPdFilter] = useState<'all' | 'y' | 'n'>('all');
  const [odrAddedToDdFilter, setOdrAddedToDdFilter] = useState<'all' | 'y' | 'n'>('n');
  const [odrEligibleFilter, setOdrEligibleFilter] = useState<'all' | 'y' | 'n'>('all');
  const [odrHomelessFilter, setOdrHomelessFilter] = useState<'all' | 'y' | 'n'>('all');
  const [odrPrePleaFilter, setOdrPrePleaFilter] = useState<'all' | 'y' | 'n'>('all');
  const [odrCancelledFilter, setOdrCancelledFilter] = useState<'all' | 'y' | 'n'>('all');
  const [odrEnrolledFilter, setOdrEnrolledFilter] = useState<'all' | 'y' | 'n'>('all');
  const [odrIhFilter, setOdrIhFilter] = useState<'all' | 'Y' | 'N'>('all');
  const [odrImdFilter, setOdrImdFilter] = useState<'all' | 'Y' | 'N'>('all');

  const [covidGroupFilter, setCovidGroupFilter] = useState<string>('all');
  const [covidDispositionFilter, setCovidDispositionFilter] = useState<string>('all');
  const [covidRetainFilter, setCovidRetainFilter] = useState<'all' | 'y' | 'n'>('all');
  const [covidReleasedFilter, setCovidReleasedFilter] = useState<'all' | 'y' | 'n'>('all');
  const [covidEnrolledFilter, setCovidEnrolledFilter] = useState<'all' | 'y' | 'n'>('all');
  const [covidCompletedFilter, setCovidCompletedFilter] = useState<'all' | 'y' | 'n'>('all');
  const [covidJacketReadyFilter, setCovidJacketReadyFilter] = useState<'all' | 'y' | 'n'>('all');
  const [covidReleasePassSentFilter, setCovidReleasePassSentFilter] = useState<'all' | 'y' | 'n'>('all');

  const [covidTestingTypeFilter, setCovidTestingTypeFilter] = useState<string>('all');
  const [covidTestingSourceFilter, setCovidTestingSourceFilter] = useState<'all' | 'staff' | 'client'>('all');
  const [covidTestingProgramFilter, setCovidTestingProgramFilter] = useState<string>('all');
  const [covidTestingResultFilter, setCovidTestingResultFilter] = useState<string>('all');
  const [covidTestingClosedFilter, setCovidTestingClosedFilter] = useState<'all' | 'y' | 'n'>('all');

  const [covidVaccineSourceFilter, setCovidVaccineSourceFilter] = useState<'all' | 'staff' | 'client'>('all');
  const [covidVaccineProgramFilter, setCovidVaccineProgramFilter] = useState<string>('all');
  const [covidVaccineShot1Filter, setCovidVaccineShot1Filter] = useState<'all' | 'Yes' | 'None'>('all');
  const [covidVaccineShot2Filter, setCovidVaccineShot2Filter] = useState<'all' | 'Yes' | 'None'>('all');

  const [influenzaProgramFilter, setInfluenzaProgramFilter] = useState<string>('all');
  const [influenzaNotReceivedFilter, setInfluenzaNotReceivedFilter] = useState<string>('all');

  const [maternalIneligibleFilter, setMaternalIneligibleFilter] = useState<'y' | 'n'>('n');

  const [inrepClosedFilter, setInrepClosedFilter] = useState<'all' | 'y' | 'n'>('all');
  const [inrepProgramFilter, setInrepProgramFilter] = useState<string>('all');
  const [inrepReportOpen, setInrepReportOpen] = useState(false);
  const [inrepStartDate, setInrepStartDate] = useState('');
  const [inrepEndDate, setInrepEndDate] = useState('');
  const [inrepReportProgram, setInrepReportProgram] = useState('');

  const [progressReportSubmittedFilter, setProgressReportSubmittedFilter] = useState<'all' | 'y' | 'n'>('all');
  const [progressReportCompletedFilter, setProgressReportCompletedFilter] = useState<'all' | 'y' | 'n'>('all');
  const [progressReportHubFilter, setProgressReportHubFilter] = useState<string>('all');
  const [progressReportAppearanceFilter, setProgressReportAppearanceFilter] = useState<string>('all');
  const [progressReportIcmsProviderFilter, setProgressReportIcmsProviderFilter] = useState('');
  const [progressReportCourtDateFilter, setProgressReportCourtDateFilter] = useState('');

  const [laiFacilityFilter, setLaiFacilityFilter] = useState<'all' | 'TTCF' | 'CRDF' | 'MCJ' | 'NCCF/PDC'>('all');
  const [laiReportOpen, setLaiReportOpen] = useState(false);
  const [psychBedReport, setPsychBedReport] = useState<string | null>(null);
  const [laiCommunityLocationFilter, setLaiCommunityLocationFilter] = useState<'all' | 'PB-PSH' | 'IH+SS-PSH+BC+Other'>('all');

  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [rowDraft, setRowDraft] = useState<Record<string, string>>({});
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<CandidateRow | null>(null);
  const [detailEditing, setDetailEditing] = useState(false);
  const [detailDraft, setDetailDraft] = useState<Record<string, string>>({});
  const [detailOdrTask, setDetailOdrTask] = useState<OdrCandidateTaskType | null>(null);
  const [courtChecklistOpen, setCourtChecklistOpen] = useState(false);
  const [detailChargeAttention, setDetailChargeAttention] = useState(false);
  const [detailFocusField, setDetailFocusField] = useState<string | null>(null);
  const detailFieldInputRefs = useRef<Record<string, HTMLElement | null>>({});
  const [generalAddOpen, setGeneralAddOpen] = useState(false);
  const [generalAddSubmitting, setGeneralAddSubmitting] = useState(false);
  const [generalAddDraft, setGeneralAddDraft] = useState<Record<string, string>>({});
  const [createdProgressReportId, setCreatedProgressReportId] = useState<number | null>(null);
  const [incidentAddClients, setIncidentAddClients] = useState<IncidentClient[]>([]);
  const [incidentDetailClients, setIncidentDetailClients] = useState<IncidentClient[]>([]);
  const [incidentDetailSubmitting, setIncidentDetailSubmitting] = useState(false);
  const [reportActionLoading, setReportActionLoading] = useState<string | null>(null);
  const [progressReportViewers, setProgressReportViewers] = useState<ProgressReportViewer[]>([]);
  const [progressReportViewerSearch, setProgressReportViewerSearch] = useState('');
  const [progressReportViewerResults, setProgressReportViewerResults] = useState<ProgressReportViewer[]>([]);
  const [progressReportCidLoading, setProgressReportCidLoading] = useState(false);
  const [progressReportIdentifier, setProgressReportIdentifier] = useState('');
  const [progressReportParticipantMatches, setProgressReportParticipantMatches] = useState<ProgressReportParticipantMatch[]>([]);
  const [progressReportParticipantSelected, setProgressReportParticipantSelected] = useState(false);
  const [progressReportPreviewOpen, setProgressReportPreviewOpen] = useState(false);
  const [progressReportPreviewValues, setProgressReportPreviewValues] = useState<Record<string, string>>({});

  const component = config?.component || '';
  const leadReferralExportSites = config?.leadReferralExportSites || [];
  const inrepReportProgramOptions = config?.inrepReportProgramOptions || [];
  const covidTestingProgramOptions = config?.covidTestingProgramOptions || [];
  const covidVaccineProgramOptions = config?.covidVaccineProgramOptions || [];
  const influenzaProgramOptions = config?.influenzaProgramOptions || [];
  const influenzaNotReceivedOptions = config?.influenzaNotReceivedOptions || [];
  const progressReportHubOptions = config?.progressReportHubOptions || [];
  const progressReportAppearanceOptions = config?.progressReportAppearanceOptions || [];
  const mistDispositionOptions = config?.mistDispositionOptions || [];
  const mistConditionalReleaseOutcomeOptions = config?.mistConditionalReleaseOutcomeOptions || [];
  const mistDate317SecondOptions = config?.mistDate317SecondOptions || [];
  const mistOutcomeOptions = config?.mistOutcomeOptions || [];
  const mistReportFields = config?.mistReportFields || [];
  const mistReportColumnWidths = config?.mistReportColumnWidths || [];
  const programFilters: CandidateProgramFilterValues = {
    admittedFilter,
    graduatedFilter,
    mistReleasedFilter,
    dispoFilter,
    trackFilter,
    mdFilter,
    clinicianFilter,
    vetFilter,
    departmentFilter,
    dshCancelledFilter,
    odrHubFilter,
    odrMhdFilter,
    odrReviewStatusFilter,
    odrWorkflowFilter,
    odrReviewerFilter,
    odrEmailedPdFilter,
    odrAddedToDdFilter,
    odrEligibleFilter,
    odrHomelessFilter,
    odrPrePleaFilter,
    odrCancelledFilter,
    odrEnrolledFilter,
    odrIhFilter,
    odrImdFilter,
    covidGroupFilter,
    covidDispositionFilter,
    covidRetainFilter,
    covidReleasedFilter,
    covidEnrolledFilter,
    covidCompletedFilter,
    covidJacketReadyFilter,
    covidReleasePassSentFilter,
    covidTestingTypeFilter,
    covidTestingSourceFilter,
    covidTestingProgramFilter,
    covidTestingResultFilter,
    covidTestingClosedFilter,
    covidVaccineSourceFilter,
    covidVaccineProgramFilter,
    covidVaccineShot1Filter,
    covidVaccineShot2Filter,
    influenzaProgramFilter,
    influenzaNotReceivedFilter,
    maternalIneligibleFilter,
    inrepClosedFilter,
    inrepProgramFilter,
    progressReportSubmittedFilter,
    progressReportCompletedFilter,
    progressReportHubFilter,
    progressReportAppearanceFilter,
    progressReportIcmsProviderFilter,
    progressReportCourtDateFilter,
    laiFacilityFilter,
    laiCommunityLocationFilter,
  };

  const buildCandidateListVariables = (overrides: Record<string, any> = {}) =>
    buildCandidateListQueryVariables({
      component,
      page,
      pageSize,
      search,
      searchField,
      flags: programFlags,
      filters: programFilters,
      overrides: {
        sortField,
        sortDirection,
        ...overrides,
      },
    });

  const { data: schemaData, error: schemaError } = useQuery(CANDIDATE_SCHEMA, {
    variables: { component },
    skip: !config,
  });

  const { data: listData, loading, error: listError, refetch } = useQuery(CANDIDATE_LIST, {
    variables: buildCandidateListVariables(),
    skip: !config,
    pollInterval: ['lai', 'inrep', 'progress-report', 'olive', 'olive-vista'].includes(programKey) && !detailOpen && !generalAddOpen && !editingRowId ? 30000 : 0,
  });

  const [runBatchUpdate, { loading: batchSaving }] = useMutation(UPDATE_CANDIDATE);
  const [runSyncProgressReportViewers] = useMutation(SYNC_PROGRESS_REPORT_VIEWERS);
  const [runCreate] = useMutation(CREATE_CANDIDATE);
  const [runDelete, { loading: deleting }] = useMutation(DELETE_CANDIDATE);
  const [reviewOdrCandidateCharges, { loading: reviewingOdrCandidateCharges }] = useMutation(
    REVIEW_ODR_CANDIDATE_CHARGES,
  );

  const { data: incidentClientData, loading: incidentClientsLoading, error: incidentClientsError } = useQuery<{ incidentReportClients: IncidentClient[] }>(INCIDENT_CLIENTS, { variables: { id: Number(detailRow?.id || 0) }, skip: !isInrepProgram || !detailOpen || !detailRow?.id, fetchPolicy: 'network-only' });
  const { data: incidentRecordData, loading: incidentRecordLoading, error: incidentRecordError } = useQuery<{ candidateById: CandidateRow | null }>(INCIDENT_RECORD, { variables: { id: Number(detailRow?.id || 0) }, skip: !isInrepProgram || !detailOpen || !detailRow?.id, fetchPolicy: 'network-only' });
  const { data: incidentOptionsData, error: incidentOptionsError } = useQuery<{ incidentReportFormOptions: IncidentFormOptions }>(INCIDENT_FORM_OPTIONS, { skip: !isInrepProgram, fetchPolicy: 'network-only' });
  const incidentDetailUnavailable = isInrepProgram && (incidentClientsLoading || incidentRecordLoading || Boolean(incidentClientsError || incidentRecordError) || !Array.isArray(incidentClientData?.incidentReportClients) || Number(incidentRecordData?.candidateById?.id) !== Number(detailRow?.id));
  const presence = useToolEditingPresence(programKey, ['lai', 'inrep', 'olive', 'olive-vista'].includes(programKey) && Boolean(user?.username), page, detailOpen ? Number(detailRow?.id || 0) || null : editingRowId, detailOpen ? detailFocusField : null);
  useEffect(() => {
    const loadedClients = incidentClientData?.incidentReportClients;
    if (Array.isArray(loadedClients)) setIncidentDetailClients(loadedClients.map((client) => Object.fromEntries(Object.entries(client).filter(([key]) => key !== '__typename'))));
  }, [incidentClientData]);

  const fieldMeta = useMemo<CandidateFieldMeta[]>(() => {
    const meta = schemaData?.candidateSchema?.fieldMeta;
    if (Array.isArray(meta)) {
      return meta as CandidateFieldMeta[];
    }

    return [];
  }, [schemaData?.candidateSchema?.fieldMeta]);

  const fields = useMemo<string[]>(() => {
    const listFields = listData?.candidateList?.fields;
    if (Array.isArray(listFields)) {
      return listFields;
    }

    const schemaFields = schemaData?.candidateSchema?.fields;
    if (Array.isArray(schemaFields)) {
      return schemaFields;
    }

    return [];
  }, [listData?.candidateList?.fields, schemaData?.candidateSchema?.fields]);

  const rows: CandidateRow[] = Array.isArray(listData?.candidateList?.rows)
    ? (listData.candidateList.rows as CandidateRow[])
    : [];

  const candidateKeyField = useMemo(() => {
    const rawKey = String(listData?.candidateList?.key || schemaData?.candidateSchema?.key || '').trim();
    if (rawKey && fields.includes(rawKey)) {
      return rawKey;
    }

    if (fields.includes('id')) {
      return 'id';
    }

    if (rawKey) {
      return rawKey;
    }

    return 'id';
  }, [fields, listData?.candidateList?.key, schemaData?.candidateSchema?.key]);

  const rawTotalRows = listData?.candidateList?.totalRows;
  const totalRows = Number.isFinite(Number(rawTotalRows)) ? Math.max(0, Number(rawTotalRows)) : 0;

  useEffect(() => {
    if (!Number.isFinite(Number(rawTotalRows))) {
      return;
    }

    const nextTotalRows = Math.max(0, Number(rawTotalRows));
    const nextTotalPages = Math.max(1, Math.ceil(nextTotalRows / pageSize));
    setCachedTotalPages(nextTotalPages);
  }, [rawTotalRows, pageSize]);

  const totalPages = cachedTotalPages;

  const labelsByField = useMemo(() => {
    const labels: Record<string, string> = {};
    for (const meta of fieldMeta) {
      labels[meta.name] = meta.label || meta.name;
    }

    labels.participant_id = 'Participant';
    if (isOdrProgram) {
      labels.emailed_pd = 'Emailed Attorney';
    }

    return labels;
  }, [fieldMeta, isOdrProgram]);

  const searchableFields = useMemo(
    () => fields.filter((field) => field !== 'id'),
    [fields],
  );

  const progressReportProviderSuggestions = useMemo(() => {
    const suggestions = new Set<string>();
    for (const row of rows) {
      const value = getCellValue(row, 'icms_provider_name').trim();
      if (value) {
        suggestions.add(value);
      }
    }

    const activeValue = progressReportIcmsProviderFilter.trim();
    if (activeValue) {
      suggestions.add(activeValue);
    }

    return Array.from(suggestions).sort((left, right) => left.localeCompare(right));
  }, [progressReportIcmsProviderFilter, rows]);

  const dataTypeByField = useMemo(() => {
    const dataTypes: Record<string, UnifiedTableDataType> = {};

    for (const meta of fieldMeta) {
      dataTypes[meta.name] =
        config?.dataTypeOverrides?.[meta.name] ||
        mapDbTypeToDataType(meta.dbType, meta.name, meta.label);
    }

    for (const field of fields) {
      if (dataTypes[field]) {
        continue;
      }

      dataTypes[field] =
        config?.dataTypeOverrides?.[field] ||
        mapDbTypeToDataType(null, field, labelsByField[field] || field);
    }

    return dataTypes;
  }, [fieldMeta, fields, labelsByField, config?.dataTypeOverrides]);

  const writableByField = useMemo(() => {
    const writable: Record<string, boolean> = {};
    for (const meta of fieldMeta) {
      writable[meta.name] = canManage && !!meta.writable && !(isInrepProgram && (incidentClientFields.has(meta.name) || ['login_name', 'login_email'].includes(meta.name) || (incidentProvider && ['closed', 'closed_date', 'serious_outcome', 'odr_comments', 'odr_lead_assigned'].includes(meta.name))));
    }
    return writable;
  }, [canManage, fieldMeta, isInrepProgram, incidentProvider]);

  const maxLengthByField = useMemo(() => {
    const maxLengthMap: Record<string, number> = {};
    for (const meta of fieldMeta) {
      maxLengthMap[meta.name] = Number(meta.maxLength || 0);
    }
    return maxLengthMap;
  }, [fieldMeta]);

  const booleanFormatByField = useMemo(() => {
    const formats: Record<string, BooleanStorageFormat> = {};
    for (const meta of fieldMeta) {
      const dbType = String(meta.dbType || '').toLowerCase();
      if (dbType.includes("'y'") && dbType.includes("'n'")) {
        formats[meta.name] = 'yn';
      } else {
        formats[meta.name] = '10';
      }
    }
    return formats;
  }, [fieldMeta]);

  const generalWritableFields = useMemo(
    () => fields.filter((field) => !!writableByField[field]),
    [fields, writableByField],
  );

  const resetGeneralAddDraft = useCallback(() => {
    const nextDraft: Record<string, string> = {};
    for (const field of generalWritableFields) {
      nextDraft[field] = '';
    }
    if (isInrepProgram) {
      nextDraft.login_name = user?.realname || '';
      nextDraft.login_email = user?.email || '';
      nextDraft.report_date = toDraftValueForEditor(new Date().toString(), 'datetime');
    }

    setGeneralAddDraft(nextDraft);
    setCreatedProgressReportId(null);
  }, [generalWritableFields, isInrepProgram, user?.realname, user?.email]);

  useEffect(() => {
    if (!incidentAddRequested) {
      incidentAddLinkHandled.current = false;
      return;
    }
    if (!canManage || generalWritableFields.length === 0 || incidentAddLinkHandled.current) return;
    incidentAddLinkHandled.current = true;
    resetGeneralAddDraft();
    setIncidentAddClients([{}]);
    setGeneralAddOpen(true);
  }, [incidentAddRequested, canManage, generalWritableFields.length, resetGeneralAddDraft]);

  const clearEditState = () => {
    setEditingRowId(null);
    setRowDraft({});
  };

  const clearDetailState = () => {
    setDetailOpen(false);
    setDetailRow(null);
    setDetailEditing(false);
    setDetailDraft({});
    setDetailOdrTask(null);
    setCourtChecklistOpen(false);
    setDetailChargeAttention(false);
    setDetailFocusField(null);
    setProgressReportViewers([]);
    setProgressReportViewerSearch('');
    setProgressReportViewerResults([]);
  };

  const applyFilterChange = (
    setter: React.Dispatch<React.SetStateAction<any>>,
    value: any,
  ) => {
    setter(value);
    setPage(1);
    clearEditState();
    clearDetailState();
  };

  const candidateProgramFilterSetters: CandidateProgramFilterSetters = {
    admittedFilter: setAdmittedFilter,
    graduatedFilter: setGraduatedFilter,
    mistReleasedFilter: setMistReleasedFilter,
    dispoFilter: setDispoFilter,
    trackFilter: setTrackFilter,
    mdFilter: setMdFilter,
    clinicianFilter: setClinicianFilter,
    vetFilter: setVetFilter,
    departmentFilter: setDepartmentFilter,
    dshCancelledFilter: setDshCancelledFilter,
    odrHubFilter: setOdrHubFilter,
    odrMhdFilter: setOdrMhdFilter,
    odrReviewStatusFilter: setOdrReviewStatusFilter,
    odrWorkflowFilter: setOdrWorkflowFilter,
    odrReviewerFilter: setOdrReviewerFilter,
    odrEmailedPdFilter: setOdrEmailedPdFilter,
    odrAddedToDdFilter: setOdrAddedToDdFilter,
    odrEligibleFilter: setOdrEligibleFilter,
    odrHomelessFilter: setOdrHomelessFilter,
    odrPrePleaFilter: setOdrPrePleaFilter,
    odrCancelledFilter: setOdrCancelledFilter,
    odrEnrolledFilter: setOdrEnrolledFilter,
    odrIhFilter: setOdrIhFilter,
    odrImdFilter: setOdrImdFilter,
    covidGroupFilter: setCovidGroupFilter,
    covidDispositionFilter: setCovidDispositionFilter,
    covidRetainFilter: setCovidRetainFilter,
    covidReleasedFilter: setCovidReleasedFilter,
    covidEnrolledFilter: setCovidEnrolledFilter,
    covidCompletedFilter: setCovidCompletedFilter,
    covidJacketReadyFilter: setCovidJacketReadyFilter,
    covidReleasePassSentFilter: setCovidReleasePassSentFilter,
    covidTestingTypeFilter: setCovidTestingTypeFilter,
    covidTestingSourceFilter: setCovidTestingSourceFilter,
    covidTestingProgramFilter: setCovidTestingProgramFilter,
    covidTestingResultFilter: setCovidTestingResultFilter,
    covidTestingClosedFilter: setCovidTestingClosedFilter,
    covidVaccineSourceFilter: setCovidVaccineSourceFilter,
    covidVaccineProgramFilter: setCovidVaccineProgramFilter,
    covidVaccineShot1Filter: setCovidVaccineShot1Filter,
    covidVaccineShot2Filter: setCovidVaccineShot2Filter,
    influenzaProgramFilter: setInfluenzaProgramFilter,
    influenzaNotReceivedFilter: setInfluenzaNotReceivedFilter,
    maternalIneligibleFilter: setMaternalIneligibleFilter,
    inrepClosedFilter: setInrepClosedFilter,
    inrepProgramFilter: setInrepProgramFilter,
    progressReportSubmittedFilter: setProgressReportSubmittedFilter,
    progressReportCompletedFilter: setProgressReportCompletedFilter,
    progressReportHubFilter: setProgressReportHubFilter,
    progressReportAppearanceFilter: setProgressReportAppearanceFilter,
    progressReportIcmsProviderFilter: setProgressReportIcmsProviderFilter,
    progressReportCourtDateFilter: setProgressReportCourtDateFilter,
    laiFacilityFilter: setLaiFacilityFilter,
    laiCommunityLocationFilter: setLaiCommunityLocationFilter,
  };

  const onProgramFilterChange = <K extends keyof CandidateProgramFilterValues>(
    key: K,
    value: CandidateProgramFilterValues[K],
  ) => {
    candidateProgramFilterSetters[key](value);
    setPage(1);
    clearEditState();
    clearDetailState();
  };

  const resetAllFilters = () => {
    setPage(1);
    setSearchField('all');
    if (programKey === 'lai') {
      setSearch('');
      setSearchDraft('');
    }
    resetCandidateProgramFilters(candidateProgramFilterSetters);

    clearEditState();
    clearDetailState();
  };

  const hasActiveProgramFilters = hasActiveCandidateProgramFilters({
    searchField,
    flags: programFlags,
    filters: programFilters,
  });

  const buildDraftForRow = useCallback(
    (row: CandidateRow): Record<string, string> => {
      const nextDraft: Record<string, string> = {};
      for (const field of fields) {
        const isOdrCharges = isOdrProgram && field === 'charges';
        if (!writableByField[field] && !isOdrCharges) {
          continue;
        }

        const currentValue = getCellValue(row, field);
        const fieldDataType = dataTypeByField[field] || 'auto';
        nextDraft[field] = toDraftValueForEditor(currentValue, fieldDataType);
      }

      return nextDraft;
    },
    [dataTypeByField, fields, isOdrProgram, writableByField],
  );

  const openDetailModal = useCallback(
    (row: CandidateRow, focusField?: string) => {
      const rowId = Number(row?.id || 0);
      if (!rowId) {
        return;
      }

      const canEdit = fields.some((field) => !!writableByField[field]);
      setDetailRow(row);
      setDetailOpen(true);
      setDetailEditing(!isOdrProgram && canEdit);
      setDetailDraft(buildDraftForRow(row));
      setDetailFocusField(focusField || null);
    },
    [buildDraftForRow, fields, isOdrProgram, writableByField],
  );

  useEffect(() => {
    const record = incidentRecordData?.candidateById;
    if (!detailOpen || !record || Number(record.id) !== Number(detailRow?.id)) return;
    setDetailRow(record);
    setDetailDraft(buildDraftForRow(record));
  }, [incidentRecordData, detailOpen, detailRow?.id, buildDraftForRow]);

  const clearOdrTaskRoute = useCallback(() => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('candidateId');
    nextSearchParams.delete('todo');
    setSearchParams(nextSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleDetailFieldChange = (field: string, value: string) => {
    setDetailFocusField(field);
    setDetailDraft((previousDraft) => ({
      ...previousDraft,
      [field]: value,
    }));
  };

  const addProgressReportViewer = (viewer: ProgressReportViewer) => {
    setProgressReportViewers((current) => {
      if (current.some((item) => item.id === viewer.id)) {
        return current;
      }

      return [...current, viewer];
    });
    setProgressReportViewerSearch('');
    setProgressReportViewerResults([]);
  };

  const removeProgressReportViewer = (viewerId: number) => {
    setProgressReportViewers((current) => current.filter((viewer) => viewer.id !== viewerId));
  };

  useEffect(() => {
    const reportId = Number(detailRow?.id || 0);
    if (!isProgressReportProgram || !detailOpen || !reportId) {
      return;
    }

    let cancelled = false;
    void apolloClient.query({
      query: PROGRESS_REPORT_VIEWERS,
      variables: { reportId },
      fetchPolicy: 'network-only',
    }).then((response) => {
      if (cancelled) {
        return;
      }
      const viewers = response.data?.progressReportViewers;
      setProgressReportViewers(Array.isArray(viewers) ? viewers : []);
    }).catch(() => {
      if (!cancelled) {
        setProgressReportViewers([]);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [apolloClient, detailOpen, detailRow?.id, isProgressReportProgram]);

  useEffect(() => {
    if (!isProgressReportProgram || (!detailOpen && !generalAddOpen)) {
      return;
    }

    const search = progressReportViewerSearch.trim();
    if (search.length < 2) {
      setProgressReportViewerResults([]);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      void apolloClient.query({
        query: SEARCH_PROGRESS_REPORT_VIEWERS,
        variables: { search },
        fetchPolicy: 'network-only',
      }).then((response) => {
        if (cancelled) {
          return;
        }
        const viewers = response.data?.searchProgressReportViewers;
        setProgressReportViewerResults(Array.isArray(viewers) ? viewers : []);
      }).catch(() => {
        if (!cancelled) {
          setProgressReportViewerResults([]);
        }
      });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [apolloClient, detailOpen, generalAddOpen, isProgressReportProgram, progressReportViewerSearch]);

  const registerDetailFieldInputRef = useCallback((field: string, element: HTMLElement | null) => {
    detailFieldInputRefs.current[field] = element;
  }, []);

  const selectAllDetailFieldValue = useCallback((element: HTMLElement | null) => {
    if (!element) {
      return;
    }

    if (element instanceof HTMLTextAreaElement) {
      element.select();
      return;
    }

    if (!(element instanceof HTMLInputElement)) {
      return;
    }

    if (element.disabled || element.readOnly) {
      return;
    }

    const inputType = String(element.type || '').toLowerCase();
    if (
      inputType === 'button' ||
      inputType === 'checkbox' ||
      inputType === 'color' ||
      inputType === 'file' ||
      inputType === 'radio' ||
      inputType === 'range' ||
      inputType === 'reset' ||
      inputType === 'submit'
    ) {
      return;
    }

    try {
      element.select();
    } catch (_error) {
      // Some browser/input combinations do not support select().
    }
  }, []);

  const handleDetailFieldFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      selectAllDetailFieldValue(event.currentTarget);
    },
    [selectAllDetailFieldValue],
  );

  const focusOdrCharges = useCallback(() => {
    setDetailChargeAttention(true);
    const chargesInput = detailFieldInputRefs.current.charges;
    if (!chargesInput) {
      return;
    }

    chargesInput.scrollIntoView({ block: 'center' });
    chargesInput.focus();
    selectAllDetailFieldValue(chargesInput);
  }, [selectAllDetailFieldValue]);

  useEffect(() => {
    if (!detailOpen) {
      return;
    }

    if (!detailEditing) {
      return;
    }

    const focusTimer = window.setTimeout(() => {
      if (detailFocusField) {
        const selectedTarget = detailFieldInputRefs.current[detailFocusField];
        if (selectedTarget) {
          selectedTarget.focus();
          selectAllDetailFieldValue(selectedTarget);
          return;
        }
      }

      const fallbackField = fields.find(
        (field) => !!writableByField[field] || (isOdrProgram && field === 'charges'),
      );
      if (!fallbackField) {
        return;
      }

      const fallbackTarget = detailFieldInputRefs.current[fallbackField];
      if (!fallbackTarget) {
        return;
      }

      fallbackTarget.focus();
      selectAllDetailFieldValue(fallbackTarget);
    }, 0);

    return () => window.clearTimeout(focusTimer);
  }, [detailEditing, detailFocusField, detailOpen, fields, isOdrProgram, selectAllDetailFieldValue, writableByField]);

  const buildUpdatesForDraft = (
    row: CandidateRow,
    draftValues: Record<string, string>,
  ): Array<{ field: string; value: string }> | null => {
    const updates: Array<{ field: string; value: string }> = [];

    for (const field of fields) {
      if (!writableByField[field]) {
        continue;
      }

      const label = labelsByField[field] || field;
      const fieldDataType = dataTypeByField[field] || 'auto';
      const booleanFormat = booleanFormatByField[field] || '10';
      const currentValue = getCellValue(row, field);
      const currentComparable = normalizeForComparison(currentValue, fieldDataType, booleanFormat);
      const draftValue = draftValues[field] ?? toDraftValueForEditor(currentValue, fieldDataType);

      const maxLength = isTextLikeDataType(fieldDataType) ? maxLengthByField[field] || 0 : 0;
      let nextValue = draftValue;
      if (maxLength > 0 && nextValue.length > maxLength) {
        nextValue = nextValue.slice(0, maxLength);
      }

      const normalized = normalizeForSaveValue(nextValue, fieldDataType, label, booleanFormat);
      if (normalized.error) {
        toast.error(normalized.error);
        return null;
      }

      nextValue = normalized.value;
      const nextComparable = normalizeForComparison(nextValue, fieldDataType, booleanFormat);
      if (nextComparable === currentComparable) {
        continue;
      }

      updates.push({ field, value: nextValue });
    }

    return updates;
  };

  const applyRowUpdates = async (rowId: number, updates: Array<{ field: string; value: string }>) => {
    if (updates.length === 0) {
      return;
    }

    const response = await runBatchUpdate({
      variables: {
        component: config.component,
        id: rowId,
        values: updates.map((update) => ({ key: update.field, value: update.value })),
        incidentClients: isInrepProgram && detailOpen ? incidentDetailClients : undefined,
      },
    });
    if (!response.data?.updateCandidate?.success || (isInrepProgram && (response.errors?.length || Number(response.data.updateCandidate.id) !== rowId))) {
      throw new Error('The row was not updated.');
    }
  };

  const refreshAfterPromotionWrite = async (action: 'created' | 'updated' | 'deleted') => {
    if (!isPromotionPage) {
      await refetch();
      return;
    }

    try {
      await refetch();
    } catch {
      const recordLabel = programKey === 'lai' ? 'patient' : 'record';
      toast.error(`The ${recordLabel} was ${action}, but the list could not be refreshed. Refresh the page.`);
    }
  };

  const syncSelectedProgressReportViewers = async (reportId: number) => {
    if (!isProgressReportProgram || !reportId) {
      return;
    }

    const response = await runSyncProgressReportViewers({
      variables: {
        reportId,
        viewerIds: progressReportViewers.map((viewer) => viewer.id),
      },
    });
    const result = response.data?.syncProgressReportViewers;
    if (!result?.success || Number(result.id) !== reportId || response.errors?.length) {
      throw new Error('Progress report viewers were not saved.');
    }
  };

  const handleSearch = (value?: string) => {
    setPage(1);
    const nextSearch = typeof value === 'string' ? value : searchDraft;
    setSearch(nextSearch.trim());
    clearEditState();
    clearDetailState();
  };

  const handleEditFieldChange = useCallback((field: string, value: string) => {
    setRowDraft((previousDraft) => ({
      ...previousDraft,
      [field]: value,
    }));
  }, []);

  const handleSaveRow = async (row: CandidateRow) => {
    const rowId = Number(row?.id || 0);
    if (!rowId) {
      return;
    }

    const updates = buildUpdatesForDraft(row, rowDraft);
    if (!updates) {
      return;
    }

    if (updates.length === 0) {
      clearEditState();
      toast.success('No changes to save.');
      return;
    }

    try {
      await applyRowUpdates(rowId, updates);
      toast.success(`Updated ${updates.length} field(s)`);
      clearEditState();
      await refreshAfterPromotionWrite('updated');
    } catch (error: unknown) {
      toast.error(writeErrorMessage(error, 'Failed to update row', 'The row was not updated.'));
    }
  };

  const handleSaveDetail = async () => {
    if (isInrepProgram && incidentDetailSubmitting) return;
    if (incidentDetailUnavailable) {
      toast.error('Load the involved clients before saving this report.');
      return;
    }
    if (isInrepProgram) {
      const errors = incidentValidationErrors(detailFormValues, incidentDetailClients);
      if (errors.length) { toast.error(errors.join(' ')); return; }
    }
    const activeRow = detailRow;
    const rowId = Number(activeRow?.id || 0);
    if (!activeRow || !rowId) {
      return;
    }

    if (isProgressReportProgram && !String(detailDraft.client_current_condition || '').trim()) {
      toast.error('Client current condition is required.');
      return;
    }

    if (isProgressReportProgram) {
      const savedValues = activeRow.cells.reduce<Record<string, string>>((values, cell) => {
        values[cell.key] = cell.value === null || cell.value === undefined ? '' : String(cell.value);
        return values;
      }, {});
      const submissionValues = { ...savedValues, ...detailDraft };
      const fieldsToValidate: string[] = submissionValues.completed === '1'
        ? ['client_current_condition']
        : [...PROGRESS_REPORT_REQUIRED_FIELDS];
      const missingFields = fieldsToValidate.filter((field) => !String(submissionValues[field] || '').trim());
      if (missingFields.length > 0) {
        toast.error(`Complete the required progress report fields: ${missingFields.map((field) => labelsByField[field] || field).join(', ')}`);
        return;
      }
      if (submissionValues.completed !== '1' && !confirmProgressReportSubmission(submissionValues)) {
        return;
      }
    }

    const updates = buildUpdatesForDraft(activeRow, detailDraft);
    if (!updates) {
      return;
    }
    if (isInrepProgram && !updates.length) updates.push({ field: 'program', value: getCellValue(activeRow, 'program') });

    const currentCharges = getCellValue(activeRow, 'charges').trim();
    const nextCharges = String(detailDraft.charges ?? currentCharges).trim();
    const chargesChanged = isOdrProgram && nextCharges !== currentCharges;
    if (chargesChanged && !nextCharges) {
      toast.error('Enter the reviewed charges before saving.');
      focusOdrCharges();
      return;
    }

    if (updates.length === 0 && !chargesChanged) {
      try {
        await syncSelectedProgressReportViewers(rowId);
        toast.success('No changes to save.');
        if (isOdrProgram) {
          setDetailEditing(false);
          setDetailFocusField(null);
          setDetailDraft(buildDraftForRow(activeRow));
        } else {
          clearDetailState();
        }
      } catch {
        toast.error('The report viewers could not be saved. Please try again.');
      }
      return;
    }

    if (isInrepProgram) setIncidentDetailSubmitting(true);
    try {
      if (chargesChanged) {
        const response = await reviewOdrCandidateCharges({
          variables: { candidateId: rowId, charges: nextCharges },
        });
        if (!response.data?.reviewOdrCandidateCharges?.success) {
          throw new Error('The reviewed charges were not saved.');
        }
      }

      await applyRowUpdates(rowId, updates);
      await syncSelectedProgressReportViewers(rowId);
      const updatedFieldCount = updates.length + (chargesChanged ? 1 : 0);
      toast.success(`Updated ${updatedFieldCount} field(s)`);
      setDetailRow((previousRow) => {
        if (!previousRow) {
          return previousRow;
        }

        if (Number(previousRow.id || 0) !== rowId) {
          return previousRow;
        }

        const updatesByField = new Map<string, string>();
        for (const update of updates) {
          updatesByField.set(update.field, update.value);
        }

        const nextCells = previousRow.cells.map((cell) => {
          if (!updatesByField.has(cell.key)) {
            return cell;
          }

          return { ...cell, value: updatesByField.get(cell.key) || '' };
        });

        return { ...previousRow, cells: nextCells };
      });
      await refreshAfterPromotionWrite('updated');
      if (isOdrProgram) {
        setDetailEditing(false);
        setDetailFocusField(null);
      } else {
        clearDetailState();
      }
    } catch (error: unknown) {
      toast.error(writeErrorMessage(error, 'Failed to update row', 'The row was not updated.'));
    } finally {
      if (isInrepProgram) setIncidentDetailSubmitting(false);
    }
  };

  const handleDeleteRow = async (rowId: number): Promise<boolean> => {
    if (!rowId) {
      return false;
    }

    const deleteTarget = isOdrProgram ? 'this candidate' : `row ${rowId}`;
    const confirmed = window.confirm(`Delete ${deleteTarget}? This cannot be undone.`);
    if (!confirmed) {
      return false;
    }

    try {
      const response = await runDelete({
        variables: {
          component: config.component,
          id: rowId,
        },
      });
      if (['lai', 'inrep', 'progress-report', 'olive', 'olive-vista'].includes(programKey) && !response.data?.deleteCandidate?.success) {
        toast.error(programKey === 'lai' ? 'The patient was not deleted. Refresh the list and try again.' : 'The record was not deleted. Refresh the list and try again.');
        return false;
      }
      clearEditState();
      if (Number(detailRow?.id || 0) === rowId) {
        clearDetailState();
      }
      toast.success(isOdrProgram ? 'Deleted candidate' : `Deleted row ${rowId}`);
      await refreshAfterPromotionWrite('deleted');
      return true;
    } catch (error: unknown) {
      const safeMessage = programKey === 'lai' ? 'The patient was not deleted. Refresh the list and try again.' : 'The record was not deleted. Refresh the list and try again.';
      toast.error(writeErrorMessage(error, 'Failed to delete row', safeMessage));
      return false;
    }
  };

  const handleDeleteFromDetail = async () => {
    const rowId = Number(detailRow?.id || 0);
    if (!rowId) {
      return;
    }

    const deleted = await handleDeleteRow(rowId);
    if (!deleted) {
      return;
    }
  };

  const handleSortChange = (nextSortField: string, nextSortDirection: 'asc' | 'desc') => {
    setSortField(nextSortField);
    setSortDirection(nextSortDirection === 'asc' ? 'ASC' : 'DESC');
    clearEditState();
    clearDetailState();
  };

  const canGoBack = page > 1;
  const canGoNext = page < totalPages;

  const columns = useMemo<UnifiedTableColumn<CandidateRow>[]>(() => {
    const headerClassName = '!bg-white';

    const tableFields = isOdrProgram
      ? fields.filter((field) => !ODR_WORKFLOW_INTERNAL_FIELDS.has(field))
      : fields;
    const dataColumns: UnifiedTableColumn<CandidateRow>[] = tableFields.map((field) => {
      const label = labelsByField[field] || field;
      const fieldDataType = dataTypeByField[field] || 'auto';
      const preserveNewlines = shouldPreserveNewlines(config, field, label);

      return {
        key: field,
        header: label,
        headerClassName,
        sortable: true,
        dataType: fieldDataType,
        preserveNewlines,
        searchValue: (row: CandidateRow) => getCellValue(row, field),
        sortValue: (row: CandidateRow) => getCellValue(row, field),
        accessor: (row: CandidateRow) => {
          const rowId = Number(row?.id || 0);
          const editable = !!writableByField[field];
          const currentValue = getCellValue(row, field);
          const isEditingRow = editingRowId === rowId;
          const isParticipantLinkField = label === 'Participant';

          if (!isEditingRow || !editable) {
            if (isParticipantLinkField) {
              const participantValue = currentValue.trim();
              if (!participantValue) {
                if (isOdrProgram) {
                  return null;
                }
                return <span className="text-[var(--neutral-gray-100)]">(empty)</span>;
              }

              return (
                <a
                  href={`/participants/${encodeURIComponent(participantValue)}`}
                  className="block w-full -mx-3 -my-2 px-3 py-2 text-left text-[var(--azure-primary)] hover:underline hover:underline-offset-2"
                >
                  {isOdrProgram ? 'Open Participant' : participantValue}
                </a>
              );
            }

            const isEmptyOdrValue = isOdrProgram && !currentValue.trim();
            const readOnlyDisplay = isEmptyOdrValue ? '' : formatForReadOnlyDisplay(currentValue, fieldDataType);
            const displayValue = preserveNewlines
              ? formatNewlineCellValue(readOnlyDisplay)
              : readOnlyDisplay;
            const truncatedValue = truncateCellValue(displayValue, TABLE_CELL_TRUNCATE_AFTER_CHARS);

            const highlightedDisplay =
              truncatedValue === '(empty)'
                ? truncatedValue
                : renderHighlightedMatch(truncatedValue, search);

            const shouldWrapCell = preserveNewlines || truncatedValue.length > TABLE_CELL_WRAP_AFTER_CHARS;

            return (
              <button
                type="button"
                aria-label={isOdrProgram ? `View ${label}` : `View ${label} for candidate ${rowId}`}
                onClick={() => openDetailModal(row, field)}
                className={cn(
                  "block w-full cursor-pointer text-left hover:underline -mx-3 -my-2 px-3 py-2",
                  shouldWrapCell && "break-words",
                  shouldWrapCell && (preserveNewlines ? "whitespace-pre-wrap" : "whitespace-normal")
                )}
                style={shouldWrapCell ? { maxWidth: `${TABLE_CELL_WRAP_AFTER_CHARS}ch` } : undefined}
              >
                {isEmptyOdrValue ? null : isBooleanDataType(fieldDataType) ? (
                  renderBooleanIcon(currentValue)
                ) : truncatedValue === '(empty)' ? (
                  <span className="text-[var(--neutral-gray-100)]">(empty)</span>
                ) : (
                  highlightedDisplay
                )}
              </button>
            );
          }

          const draftValue = rowDraft[field] ?? toDraftValueForEditor(currentValue, fieldDataType);
          const editorKind = getEditorKindForDataType(fieldDataType);
          const maxLength = isTextLikeDataType(fieldDataType) ? maxLengthByField[field] || undefined : undefined;

          if (editorKind === 'boolean') {
            const booleanFormat = booleanFormatByField[field] || '10';
            const yesValue = booleanFormat === 'yn' ? 'y' : '1';
            const noValue = booleanFormat === 'yn' ? 'n' : '0';
            const currentBooleanValue = toBooleanStorageValue(draftValue, booleanFormat) || '';
            const groupName = `row-${rowId}-${field}-boolean`;

            return (
              <div className="flex min-w-[180px] items-center justify-start gap-3 pl-1">
                <label className="flex items-center gap-1.5 text-xs text-[var(--neutral-gray-120)]">
                  <input
                    type="radio"
                    name={groupName}
                    checked={currentBooleanValue === yesValue}
                    onChange={() => handleEditFieldChange(field, yesValue)}
                  />
                  Yes
                </label>
                <label className="flex items-center gap-1.5 text-xs text-[var(--neutral-gray-120)]">
                  <input
                    type="radio"
                    name={groupName}
                    checked={currentBooleanValue === noValue}
                    onChange={() => handleEditFieldChange(field, noValue)}
                  />
                  No
                </label>
                <label className="flex items-center gap-1.5 text-xs text-[var(--neutral-gray-120)]">
                  <input
                    type="radio"
                    name={groupName}
                    checked={currentBooleanValue === ''}
                    onChange={() => handleEditFieldChange(field, '')}
                  />
                  Unknown
                </label>
              </div>
            );
          }

          if (editorKind === 'number') {
            return (
              <Input
                type="number"
                value={draftValue}
                step={isIntegerDataType(fieldDataType) ? 1 : 'any'}
                onChange={(event) => handleEditFieldChange(field, event.target.value)}
                className="h-8 min-w-[140px]"
              />
            );
          }

          if (isMistProgram && field === 'facility') {
            return (
              <Input
                list="mist-facility-options"
                value={draftValue}
                onChange={(event) => handleEditFieldChange(field, event.target.value)}
                maxLength={maxLength}
                className="h-8 min-w-[140px]"
              />
            );
          }

          const inputType = editorKind === 'datetime-local' ? 'datetime-local' : editorKind;

          return (
            <Input
              type={inputType}
              value={draftValue}
              onChange={(event) => handleEditFieldChange(field, event.target.value)}
              maxLength={maxLength}
              className="h-8 min-w-[140px]"
            />
          );
        },
      };
    });

    if (isOdrProgram) {
      dataColumns.unshift({
        key: '__workflow_status',
        header: 'Status',
        headerClassName,
        sortable: false,
        align: 'center',
        width: '64px',
        accessor: (row: CandidateRow) => {
          const rowId = Number(row?.id || 0);
          const stage = getCellValue(row, 'lifecycleStage') || 'received';
          const status = getOdrWorkflowPresentation(stage);
          return (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    id={`odr-candidate-${rowId}-status`}
                    type="button"
                    aria-label={`${status.label}. Open candidate`}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-[var(--neutral-gray-20)]"
                    onClick={() => openDetailModal(row)}
                  >
                    <OdrWorkflowStatusIcon stage={stage} showTitle={false} />
                  </button>
                </TooltipTrigger>
                <TooltipContent className={cn(
                  status.className,
                  'bg-[color:var(--odr-workflow-color)]',
                  ['received', 'clinician_assigned', 'participant_created', 'closed'].includes(status.value)
                    ? 'text-black dark:text-[var(--neutral-white)]'
                    : 'text-[var(--neutral-white)]'
                )}>
                  {status.label}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        },
      });
    }

    if (isProgressReportProgram) {
      dataColumns.push({
        key: '__actions',
        header: 'Actions',
        headerClassName,
        sortable: false,
        accessor: (row: CandidateRow) => (
          <Button type="button" variant="outline" size="sm" className="h-7 gap-1.5" onClick={() => openDetailModal(row)}>
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </Button>
        ),
      });
    }

    return dataColumns;
  }, [
    dataTypeByField,
    editingRowId,
    fields,
    handleEditFieldChange,
    labelsByField,
    maxLengthByField,
    openDetailModal,
    rowDraft,
    search,
    writableByField,
    config,
    isOdrProgram,
    isProgressReportProgram,
  ]);

  const detailRowId = Number(detailRow?.id || 0);
  const detailBusy = batchSaving
    || deleting
    || reviewingOdrCandidateCharges
    || incidentDetailSubmitting;
  const incidentDetailDisabled = isInrepProgram && (detailBusy || incidentDetailUnavailable);
  const detailAttorneyEmailed = detailRow ? isOdrAttorneyEmailed(detailRow) : false;
  const detailOdrStage = detailRow ? getCellValue(detailRow, 'lifecycleStage') || 'received' : 'received';
  const detailOdrStatus = getOdrWorkflowPresentation(detailOdrStage);
  const detailChargesReviewed = !!detailRow && !!getCellValue(detailRow, 'charges_reviewed_at');
  const detailClinicianAssigned = !!detailRow && !!getCellValue(detailRow, 'clinician');
  const detailClinicianNotified = !!detailRow && !!getCellValue(detailRow, 'clinician_notified_at');
  // Moving the inline panel must retain affidavit access for linked, completed candidates.
  const detailHasParticipant = !!detailRow && Number(getCellValue(detailRow, 'participant')) > 0;
  const detailAffidavitActionAvailable = [
    'clinician_review_complete',
    'participant_created',
    'affidavit_generated',
    'affidavit_sent',
  ].includes(detailOdrStage);
  const detailAttorneyEmailAvailable = !detailAttorneyEmailed && [
    'clinician_review_complete',
    'participant_created',
    'affidavit_generated',
    'affidavit_sent',
    'closed',
  ].includes(detailOdrStage);
  const detailAddToDdAvailable = !detailHasParticipant
    && detailOdrStage === 'affidavit_sent'
    && detailAttorneyEmailed;
  const detailFields = isOdrProgram
    ? fields.filter((field) => !ODR_WORKFLOW_INTERNAL_FIELDS.has(field))
    : fields;
  const detailFormValues = fields.reduce<Record<string, string>>((values, field) => {
    const currentValue = detailRow ? getCellValue(detailRow, field) : '';
    values[field] = detailDraft[field] ?? toDraftValueForEditor(currentValue, dataTypeByField[field] || 'auto');
    return values;
  }, {});
  const refreshActiveDetailRow = async () => {
    const response = await refetch();
    if (isOdrProgram && detailRowId > 0) {
      const detailResponse = await apolloClient.query<{ candidateById: CandidateRow | null }>({
        query: ODR_CANDIDATE_RECORD,
        variables: { id: detailRowId },
        fetchPolicy: 'network-only',
      });
      const refreshedRow = detailResponse.data?.candidateById;
      if (refreshedRow) {
        setDetailRow(refreshedRow);
        setDetailDraft(buildDraftForRow(refreshedRow));
      }
      return;
    }
    const refreshedRows = Array.isArray(response.data?.candidateList?.rows)
      ? response.data.candidateList.rows as CandidateRow[]
      : [];
    const refreshedRow = refreshedRows.find((row) => Number(row.id) === detailRowId);
    if (refreshedRow) {
      setDetailRow(refreshedRow);
      setDetailDraft(buildDraftForRow(refreshedRow));
    }
  };
  const progressReportDetailWritableByField = (() => {
    if (!isProgressReportProgram || detailFormValues.completed !== '1') {
      return writableByField;
    }

    return fields.reduce<Record<string, boolean>>((writable, field) => {
      writable[field] = field === 'client_current_condition' || field === 'appearance';
      return writable;
    }, {});
  })();
  const detailHeaderTitle = (() => {
    if (!detailRow) {
      if (detailRowId) {
        return `Candidate Row ${detailRowId}`;
      }

      return 'Candidate';
    }

    const findFirstNonEmptyValue = (matcher: (field: string, label: string) => boolean): string => {
      for (const field of fields) {
        const label = labelsByField[field] || field;
        if (!matcher(field, label)) {
          continue;
        }

        const value = getCellValue(detailRow, field).trim();
        if (!value) {
          continue;
        }

        return value;
      }

      return '';
    };

    const displayName = findFirstNonEmptyValue((field, label) => /display\s*name/i.test(label) || /display\s*name/i.test(field));
    const progressReportClientName = getCellValue(detailRow, 'client_name').trim();
    if (isProgressReportProgram && progressReportClientName) {
      return `Edit Progress Report · ${progressReportClientName}`;
    }
    const bookingPreferred = findFirstNonEmptyValue(
      (_field, label) => /booking/i.test(label) && /(#|number)/i.test(label),
    );
    const bookingFallback = findFirstNonEmptyValue((field, label) => /booking/i.test(label) || /booking/i.test(field));
    const booking = bookingPreferred || bookingFallback;

    if (displayName && booking) {
      return `${displayName} \u00b7 Booking ${booking}`;
    }

    if (displayName) {
      return displayName;
    }

    if (booking) {
      return `Booking ${booking}`;
    }

    if (detailRowId) {
      return `Candidate Row ${detailRowId}`;
    }

    return 'Candidate';
  })();

  const parseReportDate = (value: string): Date | null => {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const parsedEpoch = Date.parse(trimmed);
    if (!Number.isNaN(parsedEpoch)) {
      return new Date(parsedEpoch);
    }

    const usDate = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
    if (usDate) {
      const month = usDate[1].padStart(2, '0');
      const day = usDate[2].padStart(2, '0');
      const rawYear = usDate[3];
      const year = rawYear.length === 2 ? `20${rawYear}` : rawYear;
      const fallbackEpoch = Date.parse(`${year}-${month}-${day}T00:00:00`);
      if (!Number.isNaN(fallbackEpoch)) {
        return new Date(fallbackEpoch);
      }
    }

    return null;
  };

  const normalizeReportToken = (value: string): string =>
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

  const formatReportDateCellValue = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }

    const statusDateParts = trimmed.split(' - ');
    if (statusDateParts.length >= 2) {
      const status = statusDateParts[0].trim().toUpperCase();
      const rawDatePart = statusDateParts.slice(1).join(' - ').trim();
      const parsedStatusDate = parseReportDate(rawDatePart);
      if (parsedStatusDate) {
        const month = String(parsedStatusDate.getMonth() + 1).padStart(2, '0');
        const day = String(parsedStatusDate.getDate()).padStart(2, '0');
        const year = String(parsedStatusDate.getFullYear());
        return status ? `${status} - ${month}/${day}/${year}` : `${month}/${day}/${year}`;
      }

      return status || trimmed;
    }

    const parsedDate = parseReportDate(trimmed);
    if (!parsedDate) {
      const normalized = trimmed.toLowerCase();
      if (normalized === 'y' || normalized === 'n') {
        return normalized.toUpperCase();
      }

      return trimmed;
    }

    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    const year = String(parsedDate.getFullYear());
    return `${month}/${day}/${year}`;
  };

  const isDateLikeReportField = (field: string, label: string): boolean => {
    const normalizedField = field.toLowerCase();
    const normalizedLabel = label.toLowerCase();
    const datePattern = /(date|dob|released|updated|created|timestamp|_at|time)$/;
    return datePattern.test(normalizedField) || datePattern.test(normalizedLabel);
  };

  const formatReportCellValue = (field: string, value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }

    const dataType = dataTypeByField[field] || 'auto';
    const label = labelsByField[field] || field;
    const isDateField =
      dataType === 'date' ||
      dataType === 'datetime' ||
      dataType === 'timestamp' ||
      isDateLikeReportField(field, label);

    if (isDateField) {
      return formatReportDateCellValue(trimmed);
    }

    return trimmed;
  };

  const sortRowsByFieldDesc = (inputRows: CandidateRow[], field: string): CandidateRow[] => {
    return [...inputRows].sort((left, right) => {
      const leftValue = getCellValue(left, field).trim();
      const rightValue = getCellValue(right, field).trim();
      const leftNumber = Number(leftValue);
      const rightNumber = Number(rightValue);

      const leftIsNumber = Number.isFinite(leftNumber);
      const rightIsNumber = Number.isFinite(rightNumber);

      if (leftIsNumber && rightIsNumber) {
        return rightNumber - leftNumber;
      }

      if (rightValue === leftValue) {
        return 0;
      }

      return rightValue.localeCompare(leftValue, undefined, { numeric: true, sensitivity: 'base' });
    });
  };

  const isTruthyFlag = (value: string): boolean => {
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      return false;
    }

    if (normalized === 'y' || normalized === 'yes' || normalized === '1' || normalized === 'true') {
      return true;
    }

    if (normalized.startsWith('y -')) {
      return true;
    }

    return false;
  };

  const fetchAllRowsForExport = async (overrides: Record<string, any> = {}) => {
    if (!component) {
      return [] as CandidateRow[];
    }

    const pageSizeForExport = 5000;
    const allRows: CandidateRow[] = [];
    let currentPage = 1;
    let totalRowsExpected = 0;

    while (true) {
      const response = await apolloClient.query({
        query: CANDIDATE_LIST,
        variables: {
          ...buildCandidateListVariables(overrides),
          page: currentPage,
          pageSize: pageSizeForExport,
        },
        fetchPolicy: 'network-only',
      });

      const payload = response.data?.candidateList;
      const pageRows = Array.isArray(payload?.rows) ? (payload.rows as CandidateRow[]) : [];
      totalRowsExpected = Number(payload?.totalRows || totalRowsExpected);

      allRows.push(...pageRows);

      if (pageRows.length < pageSizeForExport) {
        break;
      }

      if (totalRowsExpected > 0 && allRows.length >= totalRowsExpected) {
        break;
      }

      currentPage += 1;
    }

    return allRows;
  };

  useEffect(() => {
    if (!isMistProgram || !component) {
      setMistFacilityOptions([]);
      return;
    }

    let cancelled = false;

    const loadFacilityOptions = async () => {
      try {
        const pageSizeForExport = 5000;
        let currentPage = 1;
        let totalRowsExpected = 0;
        let fetchedRowsCount = 0;
        const values = new Set<string>();

        while (true) {
          const response = await apolloClient.query({
            query: CANDIDATE_LIST,
            variables: {
              component,
              page: currentPage,
              pageSize: pageSizeForExport,
              search: null,
              searchField: null,
              released: 'all',
              sortField: 'id',
              sortDirection: 'DESC',
            },
            fetchPolicy: 'network-only',
          });

          const payload = response.data?.candidateList;
          const pageRows = Array.isArray(payload?.rows) ? (payload.rows as CandidateRow[]) : [];
          totalRowsExpected = Number(payload?.totalRows || totalRowsExpected);
          fetchedRowsCount += pageRows.length;

          for (const row of pageRows) {
            const facilityValue = getCellValue(row, 'facility').trim();
            if (facilityValue) {
              values.add(facilityValue);
            }
          }

          if (pageRows.length < pageSizeForExport) {
            break;
          }

          if (totalRowsExpected > 0 && fetchedRowsCount >= totalRowsExpected) {
            break;
          }

          currentPage += 1;
        }

        if (cancelled) {
          return;
        }

        const nextOptions = Array.from(values).sort((left, right) => left.localeCompare(right));
        setMistFacilityOptions(nextOptions);
      } catch (_error) {
        if (cancelled) {
          return;
        }

        setMistFacilityOptions([]);
      }
    };

    void loadFacilityOptions();

    return () => {
      cancelled = true;
    };
  }, [apolloClient, component, isMistProgram]);

  // Court matching uses the full accessible ODR list without changing the user's current filters.
  const loadOdrCourtCandidates = () => fetchAllRowsForExport({
    search: null, searchField: null, cancelled: 'all', hub: 'all', odrMhd: 'all',
    reviewStatus: 'all', workflowProgress: 'all', reviewer: null, emailedPd: 'all',
    addedToDd: 'all', eligible: 'all', homeless: 'all', prePlea: 'all',
    enrolled: 'all', ih: 'all', imd: 'all', sortField: candidateKeyField || 'id', sortDirection: 'ASC',
  });

  const downloadRowsAsSpreadsheet = (rowsToDownload: CandidateRow[], filePrefix: string) => {
    if (rowsToDownload.length === 0) {
      return;
    }

    const baseFieldsToExport = fields.length
      ? fields
      : rowsToDownload[0]?.cells?.map((cell) => cell.key).filter(Boolean) || [];
    const fieldsToExport = isProgressReportProgram
      ? baseFieldsToExport.filter((field) => !['probation_status', 'recommendation_selection', 'recommendation_other_text'].includes(field))
      : baseFieldsToExport;

    const records = rowsToDownload.map((row) => {
      const record: Record<string, string> = {};
      for (const field of fieldsToExport) {
        const label = labelsByField[field] || field;
        let value = getCellValue(row, field);
        if (isProgressReportProgram && field === 'recommendation') {
          value = [
            getCellValue(row, 'recommendation_selection'),
            getCellValue(row, 'recommendation'),
            getCellValue(row, 'recommendation_other_text'),
          ].filter((part) => part.trim()).join('\r\n');
        }
        if (isProgressReportProgram && field === 'appearance') {
          const normalized = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_');
          value = normalized === 'appearance' ? 'Appearance' : 'Non-Appearance';
          if (normalized === 'virtual_appearance') {
            value = 'Virtual-Appearance';
          }
        }
        if (isProgressReportProgram && field === 'client_current_condition') {
          const conditionLabels: Record<string, string> = {
            well: 'Well',
            not_well: 'Not Well',
            graduation_encouragement: 'Graduation/Encouragement',
          };
          value = conditionLabels[value.trim()] || value;
        }
        record[label] = formatReportCellValue(field, value);
      }
      return record;
    });

    const worksheet = XLSX.utils.json_to_sheet(records);
    const workbook = XLSX.utils.book_new();
    const normalizedSheetName = normalizeReportToken(filePrefix).slice(0, 31) || 'report';
    XLSX.utils.book_append_sheet(workbook, worksheet, normalizedSheetName);

    const dateStamp = new Date().toISOString().slice(0, 10);
    const fileName = `${normalizeReportToken(filePrefix) || 'report'}_${dateStamp}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const runCandidateReport = async (fieldsMap: Record<string, string>) => {
    const action = String(fieldsMap.action || 'all-report').trim();
    const loadingToken = `${action}-${Date.now()}`;
    if (reportActionLoading) {
      return;
    }

    setReportActionLoading(loadingToken);
    try {
      if (isProgressReportProgram) {
        const response = await apolloClient.query<{ progressReportExport: CandidateAllFieldsReport }>({ query: PROGRESS_REPORT_EXPORT, fetchPolicy: 'network-only' });
        if (!response.data?.progressReportExport || response.errors?.length) throw new Error('The report could not be downloaded.');
        const bytes = await buildCandidateAllFieldsReportWorkbook(response.data.progressReportExport, 'Progress Reports');
        saveAs(new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `all-fields-progress-report_${new Date().toISOString().slice(0, 10)}.xlsx`);
        toast.success('Report downloaded.');
        return;
      }
      if (isInrepProgram) {
        const response = await apolloClient.query<{ incidentReportExport: CandidateAllFieldsReport }>({ query: INCIDENT_REPORT_EXPORT, variables: { program: fieldsMap.program || null, startDate: fieldsMap.start_date || null, endDate: fieldsMap.end_date || null }, fetchPolicy: 'network-only' });
        if (!response.data?.incidentReportExport || response.errors?.length) throw new Error('The report could not be downloaded.');
        const bytes = await buildCandidateAllFieldsReportWorkbook(response.data.incidentReportExport, 'Incidents');
        saveAs(new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `all-fields-incidents-report_${new Date().toISOString().slice(0, 10)}.xlsx`);
        setInrepReportOpen(false);
        toast.success('Report downloaded.');
        return;
      }
      const exportSortField = candidateKeyField || 'id';
      const variableOverrides: Record<string, any> = {
        sortField: exportSortField,
        sortDirection: 'DESC',
      };
      if (isProgressReportProgram) Object.assign(variableOverrides, { search: '', searchField: 'all', progressIsDraft: 'all', progressCompleted: 'all', progressHub: 'all', progressAppearance: 'all', icmsProvider: 'all', progressCourtDate: '' });
      if (isInrepProgram && fieldsMap.program) {
        variableOverrides.inrepProgram = fieldsMap.program;
      }

      const allRows = await fetchAllRowsForExport(variableOverrides);
      let filteredRows = allRows;

      if (action === 'cbr-report') {
        filteredRows = filteredRows.filter((row) => isTruthyFlag(getCellValue(row, 'cbr')));
      }

      if (action === 'off-ramp-report') {
        filteredRows = filteredRows.filter((row) => isTruthyFlag(getCellValue(row, 'off_ramp')));
      }

      if (action === 'track-report') {
        filteredRows = filteredRows.filter((row) => getCellValue(row, 'track').trim() !== '');
      }

      if (action === 'vetting-report') {
        filteredRows = filteredRows.filter((row) => isTruthyFlag(getCellValue(row, 'vet')));
      }

      if (action === 'list-releases') {
        filteredRows = filteredRows.filter((row) => getCellValue(row, 'released').trim() !== '');
      }

      if (isLeadProgram && fieldsMap.site) {
        const site = fieldsMap.site.trim().toLowerCase();
        filteredRows = filteredRows.filter((row) => getCellValue(row, 'site').trim().toLowerCase() === site);
      }

      if (isInrepProgram && (fieldsMap.start_date || fieldsMap.end_date)) {
        const startDate = parseReportDate(String(fieldsMap.start_date || ''));
        const endDate = parseReportDate(String(fieldsMap.end_date || ''));

        if (startDate && endDate && startDate > endDate) {
          toast.error('Start date cannot be later than end date.');
          return;
        }

        filteredRows = filteredRows.filter((row) => {
          const sourceDate =
            getCellValue(row, 'incident_date') ||
            getCellValue(row, 'report_date') ||
            getCellValue(row, 'updated');
          const rowDate = parseReportDate(sourceDate);
          if (!rowDate) {
            return false;
          }

          if (startDate && rowDate < startDate) {
            return false;
          }

          if (endDate) {
            const endDateInclusive = new Date(endDate);
            endDateInclusive.setHours(23, 59, 59, 999);
            if (rowDate > endDateInclusive) {
              return false;
            }
          }

          return true;
        });
      }

      const sortedRows = sortRowsByFieldDesc(filteredRows, exportSortField);

      if (sortedRows.length === 0) {
        toast.error('No rows matched this report.');
        return;
      }

      const reportIdSuffix = fieldsMap.report_id ? `_${fieldsMap.report_id}` : '';
      const siteSuffix = fieldsMap.site ? `_${fieldsMap.site}` : '';
      const prefix = `${component}_${action}${reportIdSuffix}${siteSuffix}`;
      downloadRowsAsSpreadsheet(sortedRows, prefix);
      toast.success('Report downloaded.');
    } catch (error: unknown) {
      if (isProgressReportProgram || isInrepProgram) {
        toast.error('The report could not be downloaded. Please try again.');
        return;
      }
      const legacyError = error as { message?: unknown } | null | undefined;
      const message = legacyError?.message ? String(legacyError.message) : 'Unknown error';
      toast.error(`Failed to generate report: ${message}`);
    } finally {
      setReportActionLoading(null);
    }
  };

  const submitLegacyReport = (fieldsMap: Record<string, string>) => {
    if (['olive', 'olive-vista'].includes(programKey)) {
      const report = fieldsMap.action === 'acute-bed-report' ? `acute-${fieldsMap.report_id}` : fieldsMap.action === 'court-list-report' ? 'court' : 'all';
      setPsychBedReport(report);
      return;
    }
    void runCandidateReport(fieldsMap);
  };

  const openLegacyCandidatesAction = (action: string) => {
    if (programKey === 'lai' && action === 'court-list-report') {
      setLaiReportOpen(true);
      return;
    }
    submitLegacyReport({ action, download: '1' });
  };

  const resetProgressReportParticipantLookup = () => {
    setProgressReportIdentifier('');
    setProgressReportParticipantMatches([]);
    setProgressReportParticipantSelected(false);
  };

  const openLegacyAddForm = () => {
    setIncidentAddClients([{}]);
    resetGeneralAddDraft();
    resetProgressReportParticipantLookup();
    setProgressReportViewers([]);
    setProgressReportViewerSearch('');
    setProgressReportViewerResults([]);
    setGeneralAddOpen(true);
  };

  const openLegacyConvertParticipant = (rowId: number) => {
    if (!rowId) {
      return;
    }

    const sourceRow = rows.find((row) => Number(row.id || 0) === rowId);
    if (!sourceRow) {
      toast.error('Candidate row was not found.');
      return;
    }

    const participant = getCellValue(sourceRow, 'participant').trim();
    if (!participant) {
      toast.error('No linked participant for this candidate.');
      return;
    }

    window.location.assign(`/participants/${encodeURIComponent(participant)}`);
  };

  const addButtonLabel = getCandidateAddButtonLabel(programFlags);

  const candidateActionButtonClass =
    '[&_button]:h-8 [&_button]:whitespace-nowrap [&_button]:text-xs';

  const candidateActionRowClass = `flex w-full flex-wrap items-center justify-start gap-2 ${candidateActionButtonClass}`;

  const renderReportActionLabel = (label: string) => (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <FileText className="h-3 w-3" aria-hidden="true" />
      <span>{label}</span>
    </span>
  );

  const renderStandardActionButtons = () => (
    <>
      {hasActiveProgramFilters && (
        <Button type="button" variant="outline" size="sm" className="h-7 whitespace-nowrap px-2 text-[11px]" onClick={resetAllFilters}>
          <RotateCcw className="h-4 w-4" />
          Reset Filters
        </Button>
      )}
    </>
  );

  const submitInrepReport = () => {
    if (inrepStartDate && inrepEndDate && inrepStartDate > inrepEndDate) {
      toast.error('Start date cannot be later than end date.');
      return;
    }

    submitLegacyReport({
      action: 'all-report',
      download: '1',
      start_date: inrepStartDate,
      end_date: inrepEndDate,
      program: inrepReportProgram,
    });
  };

  const setGeneralAddField = (field: string, value: string) => {
    setGeneralAddDraft((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const validateProgressReportDraft = (draft: Record<string, string>, createMode: boolean): boolean => {
    if (!isProgressReportProgram) {
      return true;
    }

    const requiredFields: string[] = [...PROGRESS_REPORT_REQUIRED_FIELDS];
    if (createMode) {
      requiredFields.unshift('participant_id');
    }

    const missing = requiredFields.filter((field) => !String(draft[field] || '').trim());
    if (missing.length === 0) {
      return true;
    }

    const missingLabels = missing.map((field) => labelsByField[field] || field).join(', ');
    toast.error(`Complete the required progress report fields: ${missingLabels}`);
    return false;
  };

  const searchProgressReportParticipants = async () => {
    const identifier = progressReportIdentifier.trim();
    if (!/^\d+$/.test(identifier)) {
      toast.error('Enter a numeric CID or participant ID.');
      return;
    }

    setProgressReportCidLoading(true);
    try {
      const response = await apolloClient.query({
        query: SEARCH_PROGRESS_REPORT_PARTICIPANTS,
        variables: { identifier },
        fetchPolicy: 'network-only',
      });
      const matches = response.data?.searchProgressReportParticipants;
      if (response.errors?.length || !Array.isArray(matches)) throw new Error('Participant search was not confirmed');
      const nextMatches = matches;
      setProgressReportParticipantMatches(nextMatches);
      if (nextMatches.length === 0) {
        toast.error('No matching active ODR or Maternal participants were found.');
      }
    } catch {
      setProgressReportParticipantMatches([]);
      toast.error('Failed to search participants. Please try again.');
    } finally {
      setProgressReportCidLoading(false);
    }
  };

  const selectProgressReportParticipant = async (match: ProgressReportParticipantMatch) => {
    setProgressReportCidLoading(true);
    try {
      const response = await apolloClient.query({
        query: PROGRESS_REPORT_PARTICIPANT_BY_CID,
        variables: { cid: match.cid, participantId: match.participantId },
        fetchPolicy: 'network-only',
      });
      if (response.errors?.length) throw new Error('Participant lookup was not confirmed');
      const cells = response.data?.progressReportParticipantByCid?.cells;
      if (!Array.isArray(cells) || cells.length === 0) {
        toast.error('No participant information was found for that selection.');
        return;
      }

      setGeneralAddDraft((current) => {
        const next = { ...current };
        for (const cell of cells) {
          const field = String(cell?.key || '');
          if (!field) {
            continue;
          }
          next[field] = toDraftValueForEditor(cell?.value, dataTypeByField[field] || 'auto');
        }
        return next;
      });
      setProgressReportParticipantSelected(true);
      setProgressReportParticipantMatches([]);
      toast.success(`Selected ${match.clientName || `participant ${match.participantId}`}.`);
    } catch {
      toast.error('Failed to load participant information. Please try again.');
    } finally {
      setProgressReportCidLoading(false);
    }
  };

  const submitGeneralCandidate = async () => {
    if (isInrepProgram) {
      const errors = incidentValidationErrors(generalAddDraft, incidentAddClients);
      if (errors.length) { toast.error(errors.join(' ')); return; }
    }
    if (isMistProgram) {
      return;
    }

    if (generalAddSubmitting) {
      return;
    }

    if (isProgressReportProgram && !progressReportParticipantSelected) {
      toast.error('Search for and select a participant first.');
      return;
    }

    if (!validateProgressReportDraft(generalAddDraft, true)) {
      return;
    }

    if (isProgressReportProgram && !createdProgressReportId && !confirmProgressReportSubmission(generalAddDraft)) {
      return;
    }

    const values: Array<{ key: string; value: string }> = [];
    for (const field of generalWritableFields) {
      const label = labelsByField[field] || field;
      const fieldDataType = dataTypeByField[field] || 'auto';
      const booleanFormat = booleanFormatByField[field] || '10';
      let nextValue = generalAddDraft[field] ?? '';

      const maxLength = isTextLikeDataType(fieldDataType) ? maxLengthByField[field] || 0 : 0;
      if (maxLength > 0 && nextValue.length > maxLength) {
        nextValue = nextValue.slice(0, maxLength);
      }

      const normalized = normalizeForSaveValue(nextValue, fieldDataType, label, booleanFormat);
      if (normalized.error) {
        toast.error(normalized.error);
        return;
      }

      if (!String(normalized.value || '').trim()) {
        continue;
      }

      values.push({ key: field, value: normalized.value });
    }

    if (values.length === 0) {
      toast.error('Enter at least one value to create a candidate.');
      return;
    }

    setGeneralAddSubmitting(true);
    try {
      let createdId = isProgressReportProgram ? createdProgressReportId : null;
      if (!createdId) {
        const response = await runCreate({
          variables: {
            component,
            values,
            incidentClients: isInrepProgram ? incidentAddClients : undefined,
          },
        });

        const created = response.data?.createCandidate;
        if (!created?.success || (isPromotionPage && (!Number.isInteger(created.id) || created.id <= 0)) || (isInrepProgram && response.errors?.length)) {
          toast.error('Failed to create candidate.');
          return;
        }
        if (created.notificationQueued === false) toast.warning('The report was saved, but the incident alert could not be queued.');
        createdId = Number(created.id || 0);
        if (isProgressReportProgram) setCreatedProgressReportId(createdId);
      }

      if (isProgressReportProgram) {
        try {
          await syncSelectedProgressReportViewers(createdId);
        } catch {
          toast.error('The report was created, but its viewers were not saved.');
          return;
        }
      }

      toast.success('Candidate created successfully.');
      setGeneralAddOpen(false);
      resetGeneralAddDraft();
      setPage(1);
      clearEditState();
      clearDetailState();
      await refreshAfterPromotionWrite('created');
    } catch (error: unknown) {
      if (isPromotionPage) {
        toast.error('Failed to create candidate.');
        return;
      }
      const detail = (error as { message?: string } | null)?.message;
      const message = detail ? String(detail) : 'Unknown error';
      toast.error(`Failed to create candidate: ${message}`);
    } finally {
      setGeneralAddSubmitting(false);
    }
  };


  const setMistAddField = (field: keyof MistAddDraft, value: string) => {
    setMistAddDraft((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const resetMistAddDraft = () => {
    setMistAddDraft(DEFAULT_MIST_ADD_DRAFT);
  };

  const fieldLabel = (field: string, fallback: string): string => labelsByField[field] || fallback;

  const submitMistCandidate = async () => {
    if (!isMistProgram) {
      return;
    }

    if (mistAddSubmitting) {
      return;
    }

    const firstName = mistAddDraft.firstname.trim();
    const lastName = mistAddDraft.lastname.trim();
    if (!firstName || !lastName) {
      toast.error('Firstname and Lastname are required.');
      return;
    }

    if (mistAddDraft.disposition_type === 'Other' && !mistAddDraft.disposition_type_other.trim()) {
      toast.error('Please specify Other for Initial ODR Disposition.');
      return;
    }

    if (
      mistAddDraft.conditional_release_outcome === 'Other' &&
      !mistAddDraft.conditional_release_outcome_other.trim()
    ) {
      toast.error('Please specify Other for Conditional Release Outcome.');
      return;
    }

    if (mistAddDraft.outcome === 'Other' && !mistAddDraft.outcome_other.trim()) {
      toast.error('Please specify Other for Placement Outcome.');
      return;
    }

    if (mistAddDraft.date_317_second === 'Other' && !mistAddDraft.date_317_second_other.trim()) {
      toast.error('Please specify Other for ODR Revised Dispo Letter.');
      return;
    }

    if (mistAddDraft.date_317_second && !mistAddDraft.date_date_317_second) {
      toast.error('Please pick a date for ODR Revised Dispo Letter.');
      return;
    }

    const values: Array<{ key: string; value: string }> = [];
    const pushValue = (key: string, value: string) => {
      const normalized = value.trim();
      if (!normalized) {
        return;
      }
      values.push({ key, value: normalized });
    };

    pushValue('firstname', mistAddDraft.firstname);
    pushValue('lastname', mistAddDraft.lastname);
    pushValue('facility', mistAddDraft.facility);
    pushValue('gender', mistAddDraft.gender);
    pushValue('dob', mistAddDraft.dob);
    pushValue('n_bk', mistAddDraft.n_bk);
    pushValue('court', mistAddDraft.court);
    pushValue('n_case', mistAddDraft.n_case);
    pushValue('p_level', mistAddDraft.p_level);
    pushValue('found_ist', mistAddDraft.found_ist);
    pushValue('max_date', mistAddDraft.max_date);
    pushValue('progress_report', mistAddDraft.progress_report);
    pushValue('disposition_type', mistAddDraft.disposition_type);
    pushValue('disposition_type_other', mistAddDraft.disposition_type_other);
    pushValue('initial_dispo_letter', mistAddDraft.initial_dispo_letter);
    pushValue('conditional_release_outcome', mistAddDraft.conditional_release_outcome);
    pushValue('conditional_release_outcome_other', mistAddDraft.conditional_release_outcome_other);
    pushValue('conditional_release', mistAddDraft.conditional_release);
    pushValue('conditional_release_outcome_date', mistAddDraft.conditional_release_outcome_date);
    pushValue('hearing_date', mistAddDraft.hearing_date);
    pushValue('date_317_second', mistAddDraft.date_317_second);
    pushValue('date_317_second_other', mistAddDraft.date_317_second_other);
    pushValue('date_date_317_second', mistAddDraft.date_date_317_second);
    pushValue('outcome', mistAddDraft.outcome);
    pushValue('outcome_other', mistAddDraft.outcome_other);
    pushValue('released', mistAddDraft.released);
    pushValue('returnee', mistAddDraft.returnee);
    pushValue('team_confidentiality', mistAddDraft.team_confidentiality);

    if (values.length === 0) {
      toast.error('At least one value is required.');
      return;
    }

    setMistAddSubmitting(true);
    try {
      const response = await runCreate({
        variables: {
          component,
          values,
        },
      });

      const created = response.data?.createCandidate;
      if (!created?.success) {
        toast.error('Failed to create candidate.');
        return;
      }

      toast.success('Candidate created successfully.');
      setMistAddOpen(false);
      resetMistAddDraft();
      setPage(1);
      clearEditState();
      clearDetailState();
      await refetch();
    } catch (error: any) {
      const message = error?.message ? String(error.message) : 'Unknown error';
      toast.error(`Failed to create candidate: ${message}`);
    } finally {
      setMistAddSubmitting(false);
    }
  };


  const formatMistReportDate = (dateValue: Date): string => {
    const month = String(dateValue.getMonth() + 1).padStart(2, '0');
    const day = String(dateValue.getDate()).padStart(2, '0');
    const year = String(dateValue.getFullYear());
    return `${month}-${day}-${year}`;
  };

  const downloadMistReport = async (reportType: 'active' | 'released' | 'all') => {
    if (!component) {
      return;
    }

    if (mistReportFields.length === 0) {
      toast.error('MIST report fields are not configured.');
      return;
    }

    if (mistReportLoading) {
      return;
    }

    let releasedFilter: string | null = null;
    if (reportType === 'active') {
      releasedFilter = 'n';
    }

    if (reportType === 'released') {
      releasedFilter = 'y';
    }
    setMistReportLoading(reportType);

    try {
      const pageSizeForExport = 5000;
      let currentPage = 1;
      let totalRowsExpected = 0;
      const allRows: CandidateRow[] = [];

      while (true) {
        const response = await apolloClient.query({
          query: CANDIDATE_LIST,
          variables: {
            component,
            page: currentPage,
            pageSize: pageSizeForExport,
            search: null,
            searchField: null,
            released: releasedFilter,
            // Keep census export deterministic: newest/highest id first (legacy uniqueid ordering).
            sortField: 'id',
            sortDirection: 'DESC',
          },
          fetchPolicy: 'network-only',
        });

        const payload = response.data?.candidateList;
        const pageRows = Array.isArray(payload?.rows) ? (payload.rows as CandidateRow[]) : [];
        totalRowsExpected = Number(payload?.totalRows || totalRowsExpected);

        allRows.push(...pageRows);

        if (pageRows.length < pageSizeForExport) {
          break;
        }

        if (totalRowsExpected > 0 && allRows.length >= totalRowsExpected) {
          break;
        }

        currentPage += 1;
      }

      const now = new Date();
      const dateFormatted = formatMistReportDate(now);
      const reportTitleSuffix =
        reportType === 'active' ? 'Active' : reportType === 'released' ? 'Released' : 'All';
      const fileNameSuffix =
        reportType === 'active' ? 'active' : reportType === 'released' ? 'released' : 'all';
      const sortedRows = sortRowsByFieldDesc(allRows, 'id');

      if (reportType === 'all') {
        const mistAllReportFields = [
          'id',
          'lastname',
          'firstname',
          'facility',
          'gender',
          'dob',
          'n_bk',
          'court',
          'n_case',
          'returnee',
          'found_ist',
          'max_date',
          'p_level',
          'team_confidentiality',
          'progress_report',
          'disposition_type',
          'initial_dispo_letter',
          'conditional_release_outcome',
          'conditional_release',
          'conditional_release_outcome_date',
          'hearing_date',
          'date_317_second',
          'outcome',
          'released',
          'updated',
        ];
        const mistAllReportHeaderAliases: Record<string, string> = {
          conditional_release: 'conditionally_released',
          conditional_release_outcome_date: 'conditional_release_date',
        };
        const toLegacyHeaderCase = (value: string): string =>
          String(value || '')
            .replace(/_/g, ' ')
            .split(' ')
            .filter(Boolean)
            .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
            .join(' ');

        const headerRow = mistAllReportFields.map((field) =>
          toLegacyHeaderCase(mistAllReportHeaderAliases[field] || field),
        );

        const bodyRows = sortedRows.map((row) =>
          mistAllReportFields.map((field) => formatReportCellValue(field, getCellValue(row, field))),
        );

        const worksheet = XLSX.utils.aoa_to_sheet([
          [`${dateFormatted} / MIST - ALL Fields`],
          headerRow,
          ...bodyRows,
        ]);

        const lastColumn = XLSX.utils.encode_col(mistAllReportFields.length - 1);
        worksheet['!cols'] = mistAllReportFields.map(() => ({ wch: 30 }));
        worksheet['!merges'] = [XLSX.utils.decode_range(`A1:${lastColumn}1`)];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'MIST All Fields');
        XLSX.writeFile(workbook, `all-fields-report_${dateFormatted}.xlsx`);

        toast.success('Downloaded MIST All Fields Report.');
        return;
      }

      const headerRow = mistReportFields.map((field, index) => {
        if (index === 0) {
          return '';
        }

        return labelsByField[field] || field;
      });

      const bodyRows = sortedRows.map((row, index) =>
        mistReportFields.map((field, fieldIndex) => {
          if (fieldIndex === 0) {
            return index + 1;
          }

          return formatReportCellValue(field, getCellValue(row, field));
        }),
      );

      const worksheet = XLSX.utils.aoa_to_sheet([
        [`${dateFormatted} / Census Report ${reportTitleSuffix}`],
        headerRow,
        ...bodyRows,
      ]);

      worksheet['!cols'] = mistReportColumnWidths.map((width) => ({ wch: width }));
      worksheet['!merges'] = [XLSX.utils.decode_range('A1:X1')];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, `Census ${reportTitleSuffix}`);
      XLSX.writeFile(workbook, `census_${dateFormatted}_${fileNameSuffix}.xlsx`);

      toast.success(`Downloaded MIST Census Report ${reportTitleSuffix}.`);
    } catch (error: any) {
      const message = error?.message ? String(error.message) : 'Unknown error';
      toast.error(`Failed to generate MIST report: ${message}`);
    } finally {
      setMistReportLoading(null);
    }
  };

  if (!config) {
    return (
      <div className="p-4 md:p-6">
        <h1 className="text-2xl font-semibold">Candidate Route Not Found</h1>
        <p className="text-sm text-[var(--neutral-gray-100)]">Unknown candidate program: {programKey}</p>
      </div>
    );
  }

  const queryError = schemaError || listError;
  if (queryError) {
    const errorMessage = isPromotionPage
      ? 'Failed to load candidates. Refresh the page and try again.'
      : `Failed to load candidates: ${queryError.message}`;
    return (
      <div className="p-4 md:p-6">
        <p role={isPromotionPage ? 'alert' : undefined} className="text-sm text-[var(--semantic-error)]">{errorMessage}</p>
      </div>
    );
  }

  const programControls = renderProgramControls({
    programKey,
    values: programFilters,
    onFilterChange: onProgramFilterChange,
    actionRowClass: candidateActionRowClass,
    renderStandardActionButtons,
    renderReportActionLabel,
    onOpenCandidatesAction: openLegacyCandidatesAction,
    onSubmitReport: submitLegacyReport,
    mistReportLoading,
    onDownloadMistReport: (reportType) => {
      void downloadMistReport(reportType);
    },
    covidTestingProgramOptions,
    covidVaccineProgramOptions,
    influenzaProgramOptions,
    influenzaNotReceivedOptions,
    inrepReportProgramOptions,
    onOpenInrepReportDialog: () => setInrepReportOpen(true),
    progressReportHubOptions,
    progressReportAppearanceOptions,
    progressReportProviderSuggestions,
    leadReferralExportSites,
  });

  return (
    <div className={cn(
      'px-0 pb-4 pt-0 sm:px-1 lg:px-1 xl:px-2',
      isOdrProgram && 'flex min-h-0 flex-1 flex-col [&>.w-full]:flex [&>.w-full]:min-h-0 [&>.w-full]:flex-1 [&>.w-full]:flex-col [&>.w-full>div:first-child]:shrink-0'
    )}>
      {presence.editors.length > 0 && <p role="status" className="mb-2 text-xs text-[var(--neutral-gray-100)]">{presence.editors.map((editor) => `${editor.username} is editing row ${editor.entityId}${editor.fieldName ? ` (${editor.fieldName})` : ''}`).join('; ')}</p>}
      {presence.error && <p role="status" className="mb-2 text-xs text-[var(--semantic-warning)]">Editor presence is unavailable.</p>}
      {programKey === 'lai' && <LaiPatientReportDialog open={laiReportOpen} onOpenChange={setLaiReportOpen} />}
      {psychBedReport && <PsychBedReportDialog component={programKey} report={psychBedReport} onClose={() => setPsychBedReport(null)} />}
      {canManage && <div className="mb-2 flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          id={`${programKey}-candidates-add`}
          className="h-8"
          onClick={() => {
            if (isMistProgram) {
              resetMistAddDraft();
              setMistAddOpen(true);
              return;
            }

            openLegacyAddForm();
          }}
          disabled={isMistProgram ? mistAddSubmitting : generalAddSubmitting}
        >
          <Plus className="h-4 w-4" />
          {isMistProgram ? '+ Add Candidate' : addButtonLabel}
        </Button>
        {isOdrProgram && (
          <Button
            id="odr-candidate-download-report-list"
            type="button"
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => setCourtChecklistOpen(true)}
          >
            <Download className="h-4 w-4" />
            Download Report List
          </Button>
        )}
      </div>}
      {programControls}
      {isMistProgram && (
        <datalist id="mist-facility-options">
          {mistFacilityOptions.map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>
      )}

      <UnifiedTable<CandidateRow>
        columns={columns}
        data={rows}
        rowKey={(row) => String(row.id)}
        getRowClassName={isOdrProgram ? getOdrCandidateRowClassName : undefined}
        className={cn(
          isOdrProgram ? 'min-h-0 flex-1' : (isProgressReportProgram ? 'h-[60vh]' : (config?.tableHeight || 'h-[66vh]')),
          isOdrProgram && '[&_th]:border-r [&_td]:border-r [&_th:last-child]:border-r-0 [&_td:last-child]:border-r-0 [&_th]:border-[var(--neutral-gray-30)] [&_td]:border-[var(--neutral-gray-30)]'
        )}
        searchEnabled
        clientSearch={false}
        searchValue={searchDraft}
        onSearchValueChange={setSearchDraft}
        onSearchSubmit={handleSearch}
        searchPlaceholder={`Search ${config.title}`}
        loading={loading}
        emptyTitle="No candidate rows found"
        emptyDescription="Try changing search or filters."
        emptyAction={
          search.trim() || hasActiveProgramFilters ? (
            <div className="flex items-center justify-center gap-2">
              {search.trim() && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearchDraft('');
                    handleSearch('');
                  }}
                >
                  Clear search
                </Button>
              )}

              {hasActiveProgramFilters && (
                <Button type="button" variant="outline" size="sm" onClick={resetAllFilters}>
                  Reset filters
                </Button>
              )}
            </div>
          ) : null
        }
        sortKey={sortField}
        sortDirection={sortDirection === 'ASC' ? 'asc' : 'desc'}
        onSortChange={handleSortChange}
        clientSort={false}
        toolbarLeft={
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-[var(--neutral-gray-100)]">Search In</span>
            <select
              value={searchField}
              onChange={(event) => applyFilterChange(setSearchField, String(event.target.value || 'all'))}
              className="h-8 min-w-[180px] rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-60)] bg-white px-2 text-xs text-[var(--neutral-gray-120)]"
              aria-label="Search field"
            >
              <option value="all">All Fields</option>
              {searchableFields.map((field) => (
                <option key={field} value={field}>
                  {fieldLabel(field, field)}
                </option>
              ))}
            </select>
          </div>
        }
        toolbarRight={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                disabled={!canGoBack}
                onClick={() => {
                  if (!canGoBack) {
                    return;
                  }

                  setPage((previousPage) => previousPage - 1);
                  clearEditState();
                  clearDetailState();
                }}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="min-w-[80px] text-center text-xs text-[var(--neutral-gray-100)]">
                {page} / {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                disabled={!canGoNext}
                onClick={() => {
                  if (!canGoNext) {
                    return;
                  }

                  setPage((previousPage) => previousPage + 1);
                  clearEditState();
                  clearDetailState();
                }}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            <select
              value={[100, 200].includes(pageSize) ? String(pageSize) : 'all'}
              onChange={(event) => {
                const rawValue = String(event.target.value || '');
                if (rawValue === 'all') {
                  const nextSize = totalRows || DEFAULT_PAGE_SIZE;
                  setPageSize(nextSize);
                  setPage(1);
                  clearEditState();
                  clearDetailState();
                  return;
                }

                const nextSize = Number(rawValue || DEFAULT_PAGE_SIZE);
                if (!Number.isFinite(nextSize) || nextSize <= 0) {
                  return;
                }

                setPageSize(nextSize);
                setPage(1);
                clearEditState();
                clearDetailState();
              }}
              className="h-8 w-[90px] rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-60)] bg-white px-2 text-xs text-[var(--neutral-gray-120)]"
              aria-label="Items per page"
            >
              {[100, 200].map((size) => (
                <option key={size} value={size}>
                  {size}/page
                </option>
              ))}
              <option value="all">All</option>
            </select>
          </div>
        }
      />

      <Dialog
        open={generalAddOpen}
        onOpenChange={(open: boolean) => {
          if (!open && generalAddSubmitting && isPromotionPage) return;
          if (!open && isProgressReportProgram && progressReportCidLoading) return;
          if (!open && !generalAddSubmitting) {
            resetGeneralAddDraft();
            resetProgressReportParticipantLookup();
            setProgressReportViewers([]);
            setProgressReportViewerSearch('');
            setProgressReportViewerResults([]);
          }
          setGeneralAddOpen(open);
        }}
      >
        <DialogContent
          className="overflow-hidden p-0"
          contentWidth={isProgressReportProgram ? '80vw' : '860px'}
          contentHeight={isProgressReportProgram ? '80vh' : undefined}
          contentMaxWidth={isProgressReportProgram ? '80vw' : '860px'}
          contentMaxHeight={isProgressReportProgram ? '80vh' : '85vh'}
        >
          <div className="flex min-h-0 flex-col">
            <DialogHeader className="shrink-0 border-b border-[var(--neutral-gray-40)] px-5 py-4">
              <DialogTitle className="text-left text-lg font-semibold text-[var(--neutral-gray-120)]">
                {addButtonLabel.replace('+ ', '')}
              </DialogTitle>
              <p className="text-left text-xs text-[var(--neutral-gray-100)]">
                {isProgressReportProgram
                  ? (progressReportParticipantSelected
                    ? 'Enter the report values and save when complete.'
                    : 'Search for the participant and select the correct person before entering report details.')
                  : 'Enter one or more values and save to create a new row.'}
              </p>
            </DialogHeader>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              {isProgressReportProgram && (
                <>
                  {!progressReportParticipantSelected && (
                    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 py-4">
                      <div>
                        <h3 className="text-base font-semibold text-[var(--neutral-gray-120)]">Find the participant</h3>
                        <p className="mt-1 text-sm text-[var(--neutral-gray-100)]">
                          Enter a CID or participant ID. Exactly eight digits searches participant and ODR participant IDs; all other lengths search CID.
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Input
                          value={progressReportIdentifier}
                          inputMode="numeric"
                          placeholder="CID or participant ID"
                          onChange={(event) => {
                            setProgressReportIdentifier(event.target.value.replace(/\D/g, ''));
                            setProgressReportParticipantMatches([]);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              void searchProgressReportParticipants();
                            }
                          }}
                        />
                        <Button type="button" onClick={() => void searchProgressReportParticipants()} disabled={progressReportCidLoading || !progressReportIdentifier.trim()}>
                          {progressReportCidLoading ? 'Searching...' : 'Search'}
                        </Button>
                      </div>
                      <div className="text-xs text-[var(--neutral-gray-80)]">
                        Current search mode: {progressReportIdentifier.length === 8 ? 'Participant / ODR Participant ID' : 'CID'}
                      </div>

                      {progressReportParticipantMatches.length > 0 && (
                        <div className="overflow-hidden rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)]">
                          {progressReportParticipantMatches.map((match) => (
                            <button
                              key={`${match.program}-${match.participantId}-${match.cid || 0}`}
                              type="button"
                              className="flex w-full items-center justify-between gap-4 border-b border-[var(--neutral-gray-30)] px-4 py-3 text-left last:border-b-0 hover:bg-[var(--neutral-gray-20)]"
                              onClick={() => void selectProgressReportParticipant(match)}
                              disabled={progressReportCidLoading}
                            >
                              <span>
                                <span className="block text-sm font-medium text-[var(--neutral-gray-120)]">{match.clientName || 'Name unavailable'}</span>
                                <span className="block text-xs text-[var(--neutral-gray-80)]">{match.program}</span>
                              </span>
                              <span className="text-right text-xs text-[var(--neutral-gray-100)]">
                                <span className="block">CID: {match.cid || '—'}</span>
                                <span className="block">Participant: {match.participantId}</span>
                                <span className="block">ODR Participant: {match.odrparticipant || '—'}</span>
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {progressReportParticipantSelected && (
                    <>
                      <div className="mb-4 flex items-center justify-between rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] bg-[var(--neutral-gray-10)] px-4 py-3">
                        <div className="text-sm text-[var(--neutral-gray-120)]">
                          <span className="font-medium">{generalAddDraft.client_name || 'Selected participant'}</span>
                          <span className="ml-3 text-xs text-[var(--neutral-gray-80)]">CID {generalAddDraft.cid || '—'} · Participant {generalAddDraft.participant_id || '—'}</span>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={generalAddSubmitting || !!createdProgressReportId}
                          onClick={() => {
                            resetGeneralAddDraft();
                            setProgressReportParticipantSelected(false);
                            setProgressReportParticipantMatches([]);
                            setProgressReportViewers([]);
                            setProgressReportViewerSearch('');
                            setProgressReportViewerResults([]);
                          }}
                        >
                          Change Participant
                        </Button>
                      </div>
                      <ProgressReportFields
                        fields={fields}
                        labelsByField={labelsByField}
                        values={generalAddDraft}
                        writableByField={generalAddSubmitting || createdProgressReportId ? {} : writableByField}
                        onChange={setGeneralAddField}
                        createMode
                      />
                      <ProgressReportViewerPicker
                        selected={progressReportViewers}
                        results={progressReportViewerResults}
                        search={progressReportViewerSearch}
                        onSearchChange={setProgressReportViewerSearch}
                        onAdd={addProgressReportViewer}
                        onRemove={removeProgressReportViewer}
                        disabled={generalAddSubmitting}
                      />
                      {createdProgressReportId && <p role="alert" className="mt-3 text-sm text-[var(--semantic-error)]">The report is saved. Retry saving its viewers, or close this editor and update the saved report.</p>}
                    </>
                  )}
                </>
              )}

              {!isProgressReportProgram && generalWritableFields.length === 0 && (
                <div className="text-sm text-[var(--neutral-gray-100)]">No writable fields are available for this program.</div>
              )}

              {isInrepProgram && <><IncidentClientsEditor prefix="inrep-add-clients" clients={incidentAddClients} onChange={setIncidentAddClients} organizations={incidentOptionsData?.incidentReportFormOptions.organizations} disabled={generalAddSubmitting} />{incidentOptionsError && <p role="alert" className="text-[var(--semantic-error)]">Incident options could not be loaded.</p>}</>}

              {!isProgressReportProgram && generalWritableFields.length > 0 && (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {generalWritableFields.map((field) => {
                    const label = labelsByField[field] || field;
                    const fieldDataType = dataTypeByField[field] || 'auto';
                    const booleanFormat = booleanFormatByField[field] || '10';
                    const maxLength = isTextLikeDataType(fieldDataType) ? maxLengthByField[field] || undefined : undefined;
                    const editorKind = getEditorKindForDataType(fieldDataType);
                    const value = generalAddDraft[field] ?? '';

                    if (isInrepProgram && incidentChoiceFields.has(field)) return <IncidentChoiceField key={field} id={`incident-add-${field}`} field={field} label={label} value={value} options={incidentOptionsData?.incidentReportFormOptions} disabled={generalAddSubmitting} onChange={(next) => { setGeneralAddField(field, next); if (field === 'organization_name_ih') setGeneralAddField('incident_location_address', incidentOptionsData?.incidentReportFormOptions.housingSites.find((site) => site.site === next)?.address || ''); }} />;

                    if (editorKind === 'boolean') {
                      const yesValue = booleanFormat === 'yn' ? 'y' : '1';
                      const noValue = booleanFormat === 'yn' ? 'n' : '0';
                      return (
                        <div key={`general-add-${field}`} className="flex flex-col gap-1.5">
                          <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{label}</label>
                          <select
                            value={value}
                            disabled={isInrepProgram && generalAddSubmitting}
                            onChange={(event) => setGeneralAddField(field, event.target.value)}
                            className="h-9 rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-60)] bg-white px-2 text-sm text-[var(--neutral-gray-120)]"
                          >
                            <option value="">--</option>
                            <option value={yesValue}>Yes</option>
                            <option value={noValue}>No</option>
                          </select>
                        </div>
                      );
                    }

                    if (editorKind === 'number') {
                      return (
                        <div key={`general-add-${field}`} className="flex flex-col gap-1.5">
                          <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{label}</label>
                          <Input
                            type="number"
                            value={value}
                            disabled={isInrepProgram && generalAddSubmitting}
                            step={isIntegerDataType(fieldDataType) ? 1 : 'any'}
                            onChange={(event) => setGeneralAddField(field, event.target.value)}
                          />
                        </div>
                      );
                    }

                    if (isLargeTextField(fieldDataType, maxLengthByField[field] || 0)) {
                      return (
                        <div key={`general-add-${field}`} className="flex flex-col gap-1.5 md:col-span-2 xl:col-span-3">
                          <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{label}</label>
                          <Textarea
                            value={value}
                            disabled={isInrepProgram && generalAddSubmitting}
                            maxLength={maxLength}
                            rows={3}
                            className="min-h-[88px]"
                            onChange={(event) => setGeneralAddField(field, event.target.value)}
                          />
                        </div>
                      );
                    }

                    return (
                      <div key={`general-add-${field}`} className="flex flex-col gap-1.5">
                        <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{label}</label>
                        <Input
                          type={editorKind === 'datetime-local' ? 'datetime-local' : editorKind}
                          value={value}
                          disabled={isInrepProgram && generalAddSubmitting}
                          maxLength={maxLength}
                          onChange={(event) => setGeneralAddField(field, event.target.value)}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="shrink-0 flex items-center justify-end gap-2 border-t border-[var(--neutral-gray-40)] px-5 py-3">
              {isInrepProgram && <Button id="incident-draft-download-pdf" type="button" variant="outline" size="sm" onClick={() => { void downloadIncidentReportPdf(generalAddDraft, incidentAddClients, labelsByField).catch(() => toast.error('The incident PDF could not be created.')); }}><Download className="h-4 w-4" />Download PDF for your records</Button>}
              {isProgressReportProgram && progressReportParticipantSelected && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setProgressReportPreviewValues(generalAddDraft);
                    setProgressReportPreviewOpen(true);
                  }}
                >
                  Preview Report
                </Button>
              )}
              <Button type="button" size="sm" onClick={() => void submitGeneralCandidate()} disabled={generalAddSubmitting || (isProgressReportProgram && !progressReportParticipantSelected)}>
                {generalAddSubmitting ? 'Saving...' : createdProgressReportId ? 'Retry viewer save' : 'Save'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setGeneralAddOpen(false);
                  resetGeneralAddDraft();
                  resetProgressReportParticipantLookup();
                  setProgressReportViewers([]);
                  setProgressReportViewerSearch('');
                  setProgressReportViewerResults([]);
                }}
                disabled={generalAddSubmitting || (isProgressReportProgram && progressReportCidLoading)}
              >
                {createdProgressReportId ? 'Close' : 'Cancel'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      <Dialog
        open={mistAddOpen}
        onOpenChange={(open: boolean) => {
          if (!open && !mistAddSubmitting) {
            resetMistAddDraft();
          }
          setMistAddOpen(open);
        }}
      >
        <DialogContent className="max-h-[85vh] max-w-5xl overflow-hidden p-0" contentWidth="960px" contentMaxWidth="960px">
          <div className="flex min-h-0 flex-col">
            <DialogHeader className="shrink-0 border-b border-[var(--neutral-gray-40)] px-5 py-4">
              <DialogTitle className="text-left text-lg font-semibold text-[var(--neutral-gray-120)]">
                Add Candidate
              </DialogTitle>
              <p className="text-left text-xs text-[var(--neutral-gray-100)]">
                Enter the MIST census candidate details. This mirrors the legacy add-candidate form fields.
              </p>
            </DialogHeader>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{fieldLabel('firstname', 'Firstname')}</label>
                  <Input value={mistAddDraft.firstname} onChange={(event) => setMistAddField('firstname', event.target.value)} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{fieldLabel('lastname', 'Lastname')}</label>
                  <Input value={mistAddDraft.lastname} onChange={(event) => setMistAddField('lastname', event.target.value)} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{fieldLabel('facility', 'Facility')}</label>
                  <Input
                    list="mist-facility-options"
                    value={mistAddDraft.facility}
                    onChange={(event) => setMistAddField('facility', event.target.value)}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{fieldLabel('gender', 'Gender')}</label>
                  <Input value={mistAddDraft.gender} onChange={(event) => setMistAddField('gender', event.target.value)} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{fieldLabel('dob', 'DOB')}</label>
                  <Input type="date" value={mistAddDraft.dob} onChange={(event) => setMistAddField('dob', event.target.value)} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{fieldLabel('n_bk', 'BK#')}</label>
                  <Input value={mistAddDraft.n_bk} onChange={(event) => setMistAddField('n_bk', event.target.value)} />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{fieldLabel('court', 'Court')}</label>
                  <Input value={mistAddDraft.court} onChange={(event) => setMistAddField('court', event.target.value)} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{fieldLabel('n_case', 'Case#')}</label>
                  <Input value={mistAddDraft.n_case} onChange={(event) => setMistAddField('n_case', event.target.value)} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{fieldLabel('p_level', 'P level')}</label>
                  <Input value={mistAddDraft.p_level} onChange={(event) => setMistAddField('p_level', event.target.value)} />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{fieldLabel('found_ist', 'IST Declaration Date')}</label>
                  <Input type="date" value={mistAddDraft.found_ist} onChange={(event) => setMistAddField('found_ist', event.target.value)} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{fieldLabel('max_date', 'Max. date')}</label>
                  <Input type="date" value={mistAddDraft.max_date} onChange={(event) => setMistAddField('max_date', event.target.value)} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{fieldLabel('progress_report', 'Initial ODR Dispo. Hearing')}</label>
                  <Input type="date" value={mistAddDraft.progress_report} onChange={(event) => setMistAddField('progress_report', event.target.value)} />
                </div>

                <div className="flex flex-col gap-1.5 md:col-span-2 xl:col-span-3">
                  <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{fieldLabel('disposition_type', 'Initial ODR Disposition')}</label>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                    <select
                      value={mistAddDraft.disposition_type}
                      onChange={(event) => setMistAddField('disposition_type', event.target.value)}
                      className="h-9 rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-60)] bg-white px-2 text-sm text-[var(--neutral-gray-120)]"
                    >
                      <option value="">--Select an option--</option>
                      {mistDispositionOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    {mistAddDraft.disposition_type === 'Other' && (
                      <Input
                        placeholder="Other description"
                        value={mistAddDraft.disposition_type_other}
                        onChange={(event) => setMistAddField('disposition_type_other', event.target.value)}
                        className="md:col-span-2"
                      />
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{fieldLabel('initial_dispo_letter', 'Initial Dispo. Letter Submitted')}</label>
                  <Input type="date" value={mistAddDraft.initial_dispo_letter} onChange={(event) => setMistAddField('initial_dispo_letter', event.target.value)} />
                </div>

                <div className="flex flex-col gap-1.5 md:col-span-2">
                  <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{fieldLabel('conditional_release_outcome', 'Conditional Release Outcome')}</label>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                    <select
                      value={mistAddDraft.conditional_release_outcome}
                      onChange={(event) => setMistAddField('conditional_release_outcome', event.target.value)}
                      className="h-9 rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-60)] bg-white px-2 text-sm text-[var(--neutral-gray-120)]"
                    >
                      <option value="">--Select an option--</option>
                      {mistConditionalReleaseOutcomeOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    {mistAddDraft.conditional_release_outcome === 'Other' && (
                      <Input
                        placeholder="Other description"
                        value={mistAddDraft.conditional_release_outcome_other}
                        onChange={(event) => setMistAddField('conditional_release_outcome_other', event.target.value)}
                        className="md:col-span-2"
                      />
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{fieldLabel('conditional_release', 'Conditionally Released')}</label>
                  <Input type="date" value={mistAddDraft.conditional_release} onChange={(event) => setMistAddField('conditional_release', event.target.value)} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{fieldLabel('conditional_release_outcome_date', 'Conditional Release Date')}</label>
                  <Input type="date" value={mistAddDraft.conditional_release_outcome_date} onChange={(event) => setMistAddField('conditional_release_outcome_date', event.target.value)} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{fieldLabel('hearing_date', 'Follow up Hearing Date (If Any)')}</label>
                  <Input type="date" value={mistAddDraft.hearing_date} onChange={(event) => setMistAddField('hearing_date', event.target.value)} />
                </div>

                <div className="flex flex-col gap-1.5 md:col-span-2 xl:col-span-3">
                  <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{fieldLabel('date_317_second', 'ODR Revised Dispo Letter (If Any)')}</label>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
                    <select
                      value={mistAddDraft.date_317_second}
                      onChange={(event) => setMistAddField('date_317_second', event.target.value)}
                      className="h-9 rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-60)] bg-white px-2 text-sm text-[var(--neutral-gray-120)]"
                    >
                      <option value="">--Select an option--</option>
                      {mistDate317SecondOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    {mistAddDraft.date_317_second === 'Other' && (
                      <Input
                        placeholder="Other description"
                        value={mistAddDraft.date_317_second_other}
                        onChange={(event) => setMistAddField('date_317_second_other', event.target.value)}
                      />
                    )}
                    <Input
                      type="date"
                      value={mistAddDraft.date_date_317_second}
                      onChange={(event) => setMistAddField('date_date_317_second', event.target.value)}
                      className={mistAddDraft.date_317_second === 'Other' ? '' : 'md:col-span-2'}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5 md:col-span-2 xl:col-span-3">
                  <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{fieldLabel('outcome', 'Placement Outcome')}</label>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                    <select
                      value={mistAddDraft.outcome}
                      onChange={(event) => setMistAddField('outcome', event.target.value)}
                      className="h-9 rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-60)] bg-white px-2 text-sm text-[var(--neutral-gray-120)]"
                    >
                      <option value="">--Select an option--</option>
                      {mistOutcomeOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    {mistAddDraft.outcome === 'Other' && (
                      <Input
                        placeholder="Other description"
                        value={mistAddDraft.outcome_other}
                        onChange={(event) => setMistAddField('outcome_other', event.target.value)}
                        className="md:col-span-2"
                      />
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{fieldLabel('released', 'Released')}</label>
                  <Input type="date" value={mistAddDraft.released} onChange={(event) => setMistAddField('released', event.target.value)} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{fieldLabel('returnee', 'Re-arrest Date')}</label>
                  <Input type="date" value={mistAddDraft.returnee} onChange={(event) => setMistAddField('returnee', event.target.value)} />
                </div>

                <div className="flex flex-col gap-1.5 md:col-span-2 xl:col-span-3">
                  <label className="text-xs font-medium text-[var(--neutral-gray-100)]">{fieldLabel('team_confidentiality', 'Confidential Notes')}</label>
                  <Textarea
                    value={mistAddDraft.team_confidentiality}
                    onChange={(event) => setMistAddField('team_confidentiality', event.target.value)}
                    rows={4}
                    className="min-h-[96px]"
                  />
                </div>
              </div>
            </div>

            <div className="shrink-0 flex items-center justify-end gap-2 border-t border-[var(--neutral-gray-40)] px-5 py-3">
              <Button type="button" size="sm" onClick={() => void submitMistCandidate()} disabled={mistAddSubmitting}>
                {mistAddSubmitting ? 'Saving...' : 'Save Candidate'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setMistAddOpen(false);
                  resetMistAddDraft();
                }}
                disabled={mistAddSubmitting}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={inrepReportOpen} onOpenChange={(open: boolean) => { if (!reportActionLoading) setInrepReportOpen(open); }}>
        <DialogContent className="max-w-lg p-0" contentWidth="640px" contentMaxWidth="640px">
          <div className="flex flex-col">
            <DialogHeader className="border-b border-[var(--neutral-gray-40)] px-5 py-4">
              <DialogTitle className="text-left text-lg font-semibold text-[var(--neutral-gray-120)]">
                All Incidents List Filters
              </DialogTitle>
              <p className="text-left text-xs text-[var(--neutral-gray-100)]">
                Pick optional incident date range and program filter before generating the report.
              </p>
            </DialogHeader>

            <div className="grid grid-cols-1 gap-3 px-5 py-4 md:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-[var(--neutral-gray-100)]" htmlFor="inrep-report-start-date">
                  Date of Incident From
                </label>
                <Input
                  id="inrep-report-start-date"
                  type="date"
                  value={inrepStartDate}
                  onChange={(event) => setInrepStartDate(event.target.value)}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-[var(--neutral-gray-100)]" htmlFor="inrep-report-end-date">
                  Date of Incident To
                </label>
                <Input
                  id="inrep-report-end-date"
                  type="date"
                  value={inrepEndDate}
                  onChange={(event) => setInrepEndDate(event.target.value)}
                />
              </div>

              <div className="flex flex-col gap-1.5 md:col-span-2">
                <label className="text-xs font-medium text-[var(--neutral-gray-100)]" htmlFor="inrep-report-program">
                  Program
                </label>
                <select
                  id="inrep-report-program"
                  value={inrepReportProgram}
                  onChange={(event) => setInrepReportProgram(event.target.value)}
                  className="h-9 rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-60)] bg-white px-2 text-sm text-[var(--neutral-gray-120)]"
                >
                  <option value="">All Programs</option>
                  {inrepReportProgramOptions.map((program) => (
                    <option key={program} value={program}>
                      {program}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-[var(--neutral-gray-40)] px-5 py-3">
              <Button type="button" variant="outline" size="sm" disabled={Boolean(reportActionLoading)} onClick={() => setInrepReportOpen(false)}>
                Cancel
              </Button>
              <Button id="incident-report-generate" type="button" size="sm" disabled={Boolean(reportActionLoading)} onClick={submitInrepReport}>
                {renderReportActionLabel('Generate Report')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={detailOpen && !detailOdrTask}
        onOpenChange={(open: boolean) => {
          if (!open && isInrepProgram && (batchSaving || incidentDetailSubmitting)) return;
          if (!open) {
            clearDetailState();
            return;
          }

          setDetailOpen(true);
        }}
      >
        <DialogContent
          className="flex flex-col overflow-hidden p-0"
          contentWidth={isOdrProgram ? 'min(1000px, 94vw)' : '80vw'}
          contentHeight="80vh"
          contentMaxWidth={isOdrProgram ? '94vw' : '80vw'}
          contentMaxHeight="80vh"
        >
          <div className="flex min-h-0 flex-1 flex-col">
            {isOdrProgram ? (
              <DialogHeader className="shrink-0 border-b border-[var(--neutral-gray-40)] px-5 py-3">
                <div className="flex flex-wrap items-start gap-2">
                  <div className="min-w-0">
                    <DialogTitle className="flex items-center gap-2 text-left text-lg font-semibold text-[var(--neutral-gray-120)]">
                      {isOdrProgram && detailRow && <OdrWorkflowStatusIcon stage={detailOdrStage} />}
                      {detailHeaderTitle}
                    </DialogTitle>
                    {isOdrProgram && detailRow && (
                      <DialogDescription className="mt-0.5 pl-7 text-left text-xs font-medium text-[var(--neutral-gray-100)]">
                        {detailOdrStatus.label}
                      </DialogDescription>
                    )}
                  </div>
                  {isOdrProgram && detailRow && (
                    <div className="ml-auto flex flex-wrap items-center justify-end gap-0.5 pr-6">
                      <Button
                        id="odr-candidate-review-charges"
                        type="button"
                        variant={detailOdrStage === 'received' ? 'default' : 'outline'}
                        size="sm"
                        className="h-6 gap-0.5 px-1 text-[11px] whitespace-nowrap disabled:opacity-100"
                        onClick={() => setDetailOdrTask('charges')}
                        disabled={detailBusy || detailChargesReviewed || detailOdrStage !== 'received'}
                      >
                        <CircleCheck className="h-3 w-3" />
                        {reviewingOdrCandidateCharges ? 'Saving...' : 'Charges Reviewed'}
                      </Button>
                      <Button
                        id="odr-candidate-assign-clinician"
                        type="button"
                        variant={['ready_to_assign_clinician', 'clinician_assigned'].includes(detailOdrStage) ? 'default' : 'outline'}
                        size="sm"
                        className="h-6 gap-0.5 px-1 text-[11px] whitespace-nowrap disabled:opacity-100"
                        onClick={() => setDetailOdrTask('assign-clinician')}
                        disabled={
                          detailBusy || !['ready_to_assign_clinician', 'clinician_assigned'].includes(detailOdrStage)
                        }
                      >
                        <UserPlus className="h-3 w-3" />
                        {detailClinicianNotified
                          ? 'Clinician Notified'
                          : detailClinicianAssigned
                          ? 'Email Clinician'
                          : 'Assign Clinician'}
                      </Button>
                      <Button
                        id="odr-candidate-complete-review"
                        type="button"
                        variant={detailOdrStage === 'assigned_to_clinician' ? 'default' : 'outline'}
                        size="sm"
                        className="h-6 gap-0.5 px-1 text-[11px] whitespace-nowrap disabled:opacity-100"
                        onClick={() => setDetailOdrTask('clinical-review')}
                        disabled={detailBusy || detailOdrStage !== 'assigned_to_clinician'}
                      >
                        <Stethoscope className="h-3 w-3" />
                        Clinician Reviewed
                      </Button>
                      <Button
                        id="odr-candidate-prepare-affidavit"
                        type="button"
                        variant={detailAffidavitActionAvailable ? 'default' : 'outline'}
                        size="sm"
                        className="h-6 gap-0.5 px-1 text-[11px] whitespace-nowrap disabled:opacity-100"
                        onClick={() => setDetailOdrTask('affidavit')}
                        disabled={detailBusy || !detailAffidavitActionAvailable}
                      >
                        <Send className="h-3 w-3" />
                        Send Affidavits
                      </Button>
                      <Button
                        id="odr-candidate-attorney-email"
                        type="button"
                        variant={detailAttorneyEmailAvailable ? 'default' : 'outline'}
                        size="sm"
                        className="h-6 gap-0.5 px-1 text-[11px] whitespace-nowrap disabled:opacity-100"
                        onClick={() => setDetailOdrTask('attorney-email')}
                        disabled={detailBusy || !detailAttorneyEmailAvailable}
                      >
                        <Send className="h-3 w-3" />
                        {detailAttorneyEmailed ? 'Attorney Emailed' : 'Email Attorney'}
                      </Button>
                      <Button
                        id="odr-candidate-add-to-dd"
                        type="button"
                        variant={detailAddToDdAvailable ? 'default' : 'outline'}
                        size="sm"
                        className="h-6 gap-0.5 px-1 text-[11px] whitespace-nowrap disabled:opacity-100"
                        onClick={() => setDetailOdrTask('add-to-dd')}
                        disabled={detailBusy || !detailAddToDdAvailable}
                      >
                        <UserPlus className="h-3 w-3" />
                        Add Client to DD
                      </Button>
                    </div>
                  )}
                </div>
              </DialogHeader>
            ) : (
            <DialogHeader className="shrink-0 border-b border-[var(--neutral-gray-40)] px-5 py-4">
              <DialogTitle className="text-left text-lg font-semibold text-[var(--neutral-gray-120)]">
                {detailHeaderTitle}
              </DialogTitle>
              <p className="text-left text-xs text-[var(--neutral-gray-100)]">
                {detailRowId ? `Row ${detailRowId}. ` : ''}
                This panel displays all row fields. Writable fields are editable in this panel.
              </p>
            </DialogHeader>
            )}

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              {isOdrProgram && detailRow && (
                <section className="mb-4">
                  <label className="flex flex-col gap-1.5 text-xs font-medium text-[var(--neutral-gray-100)]">
                    Charges
                    {detailEditing && !detailChargesReviewed ? (
                      <Textarea
                        id="odr-candidate-detail-charges"
                        ref={(element) => registerDetailFieldInputRef('charges', element)}
                        value={detailFormValues.charges || ''}
                        onChange={(event) => {
                          handleDetailFieldChange('charges', event.target.value);
                          if (event.target.value.trim()) {
                            setDetailChargeAttention(false);
                          }
                        }}
                        rows={3}
                        placeholder="Enter or review charges"
                        className={cn(
                          'bg-white text-sm text-[var(--neutral-gray-120)]',
                          detailChargeAttention && 'border-2 border-[var(--semantic-error)]',
                        )}
                      />
                    ) : (
                      <div className="min-h-[2.25rem] whitespace-pre-wrap rounded-[var(--border-radius-small)] bg-[var(--neutral-gray-10)] px-2 py-2 text-sm font-normal text-[var(--neutral-gray-120)]">
                        {detailFormValues.charges || ''}
                      </div>
                    )}
                  </label>
                  {detailEditing && !detailChargesReviewed && (
                    <p className="mt-1.5 text-xs text-[var(--neutral-gray-100)]">
                      Saving a change to charges also marks the charges reviewed.
                    </p>
                  )}
                </section>
              )}
              {isProgressReportProgram && detailRow && (
                <>
                  <ProgressReportFields
                    fields={fields}
                    labelsByField={labelsByField}
                    values={detailFormValues}
                    writableByField={progressReportDetailWritableByField}
                    onChange={handleDetailFieldChange}
                  />
                  <ProgressReportViewerPicker
                    selected={progressReportViewers}
                    results={progressReportViewerResults}
                    search={progressReportViewerSearch}
                    onSearchChange={setProgressReportViewerSearch}
                    onAdd={addProgressReportViewer}
                    onRemove={removeProgressReportViewer}
                    disabled={detailFormValues.completed === '1'}
                  />
                </>
              )}

              {!isProgressReportProgram && (
              <>
              {isInrepProgram && <>
                {(incidentClientsError || (!incidentClientsLoading && !Array.isArray(incidentClientData?.incidentReportClients))) && <p role="alert" className="text-[var(--semantic-error)]">Failed to load involved clients.</p>}
                {(incidentRecordError || (!incidentRecordLoading && Number(incidentRecordData?.candidateById?.id) !== detailRowId)) && <p role="alert" className="text-[var(--semantic-error)]">Failed to load incident report.</p>}
                <IncidentClientsEditor prefix="inrep-edit-clients" clients={incidentDetailClients} onChange={setIncidentDetailClients} organizations={incidentOptionsData?.incidentReportFormOptions.organizations} disabled={!detailEditing || incidentDetailDisabled} />
              </>}
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {detailFields.filter((field) => !(isOdrProgram && field === 'charges')).map((field) => {
                  const label = labelsByField[field] || field;
                  const fieldDataType = dataTypeByField[field] || 'auto';
                  const preserveNewlines = shouldPreserveNewlines(config, field, label);
                  const writable = !!writableByField[field];
                  const currentValue = detailRow ? getCellValue(detailRow, field) : '';
                  const draftValue = detailDraft[field] ?? toDraftValueForEditor(currentValue, fieldDataType);
                  const maxLength = isTextLikeDataType(fieldDataType) ? maxLengthByField[field] || undefined : undefined;
                  const editorKind = getEditorKindForDataType(fieldDataType);
                  const isEmptyOdrValue = isOdrProgram && !currentValue.trim();
                  const readOnlyDisplay = isEmptyOdrValue ? '' : formatForReadOnlyDisplay(currentValue, fieldDataType);
                  const readOnlyValue = preserveNewlines ? formatNewlineCellValue(readOnlyDisplay) : readOnlyDisplay;

                  if (isInrepProgram && detailEditing && writable && incidentChoiceFields.has(field)) return <IncidentChoiceField key={field} id={`incident-edit-${field}`} field={field} label={label} value={draftValue} options={incidentOptionsData?.incidentReportFormOptions} disabled={incidentDetailDisabled} onChange={(next) => { handleDetailFieldChange(field, next); if (field === 'organization_name_ih') handleDetailFieldChange('incident_location_address', incidentOptionsData?.incidentReportFormOptions.housingSites.find((site) => site.site === next)?.address || ''); }} />;

                  return (
                    <div key={`detail-${field}`} className="flex flex-col gap-1.5">
                      <div className="text-xs font-medium text-[var(--neutral-gray-100)]">{label}</div>

                      {detailEditing && writable && editorKind === 'boolean' && (
                        <div className="flex w-full items-center justify-start gap-4 pl-1">
                          {(() => {
                            const booleanFormat = booleanFormatByField[field] || '10';
                            const yesValue = booleanFormat === 'yn' ? 'y' : '1';
                            const noValue = booleanFormat === 'yn' ? 'n' : '0';
                            const currentBooleanValue = toBooleanStorageValue(draftValue, booleanFormat) || '';
                            const groupName = `detail-${detailRowId}-${field}-boolean`;

                            return (
                              <>
                                <label className="flex items-center gap-1.5 text-sm text-[var(--neutral-gray-120)]">
                                  <input
                                    type="radio"
                                    disabled={incidentDetailDisabled}
                                    ref={(element) => registerDetailFieldInputRef(field, element)}
                                    name={groupName}
                                    checked={currentBooleanValue === yesValue}
                                    onChange={() => handleDetailFieldChange(field, yesValue)}
                                  />
                                  Yes
                                </label>
                                <label className="flex items-center gap-1.5 text-sm text-[var(--neutral-gray-120)]">
                                  <input
                                    type="radio"
                                    disabled={incidentDetailDisabled}
                                    name={groupName}
                                    checked={currentBooleanValue === noValue}
                                    onChange={() => handleDetailFieldChange(field, noValue)}
                                  />
                                  No
                                </label>
                                <label className="flex items-center gap-1.5 text-sm text-[var(--neutral-gray-120)]">
                                  <input
                                    type="radio"
                                    disabled={incidentDetailDisabled}
                                    name={groupName}
                                    checked={currentBooleanValue === ''}
                                    onChange={() => handleDetailFieldChange(field, '')}
                                  />
                                  Unknown
                                </label>
                              </>
                            );
                          })()}
                        </div>
                      )}

                      {detailEditing && writable && editorKind === 'number' && (
                        <Input
                          type="number"
                          ref={(element) => registerDetailFieldInputRef(field, element)}
                          disabled={incidentDetailDisabled}
                          value={draftValue}
                          step={isIntegerDataType(fieldDataType) ? 1 : 'any'}
                          onChange={(event) => handleDetailFieldChange(field, event.target.value)}
                          onFocus={handleDetailFieldFocus}
                          className="h-9"
                        />
                      )}

                      {detailEditing && writable && isMistProgram && field === 'facility' && (
                        <Input
                          list="mist-facility-options"
                          ref={(element) => registerDetailFieldInputRef(field, element)}
                          value={draftValue}
                          onChange={(event) => handleDetailFieldChange(field, event.target.value)}
                          onFocus={handleDetailFieldFocus}
                          maxLength={maxLength}
                          className="h-9"
                        />
                      )}

                      {detailEditing &&
                        writable &&
                        !(isMistProgram && field === 'facility') &&
                        editorKind !== 'boolean' &&
                        editorKind !== 'number' &&
                        isLargeTextField(fieldDataType, maxLengthByField[field] || 0) && (
                          <Textarea
                            ref={(element) => registerDetailFieldInputRef(field, element)}
                            disabled={incidentDetailDisabled}
                            value={draftValue}
                            onChange={(event) => handleDetailFieldChange(field, event.target.value)}
                            onFocus={handleDetailFieldFocus}
                            maxLength={maxLength}
                            rows={3}
                            className="min-h-[88px]"
                          />
                        )}

                      {detailEditing &&
                        writable &&
                        !(isMistProgram && field === 'facility') &&
                        editorKind !== 'boolean' &&
                        editorKind !== 'number' &&
                        !isLargeTextField(fieldDataType, maxLengthByField[field] || 0) && (
                          <Input
                            ref={(element) => registerDetailFieldInputRef(field, element)}
                            disabled={incidentDetailDisabled}
                            type={editorKind === 'datetime-local' ? 'datetime-local' : editorKind}
                            value={draftValue}
                            onChange={(event) => handleDetailFieldChange(field, event.target.value)}
                            onFocus={handleDetailFieldFocus}
                            maxLength={maxLength}
                            className="h-9"
                          />
                        )}

                      {(!detailEditing || !writable) && (
                        <div className="flex min-h-[2.25rem] items-center whitespace-pre-wrap rounded-[var(--border-radius-small)] bg-[var(--neutral-gray-10)] px-2 py-2 text-sm text-[var(--neutral-gray-120)]">
                          {isEmptyOdrValue ? null : isOdrProgram && field === 'participant' && currentValue.trim()
                            ? (
                              <a
                                href={`/participants/${encodeURIComponent(currentValue.trim())}`}
                                className="text-[var(--azure-primary)] hover:underline hover:underline-offset-2"
                              >
                                Open Participant
                              </a>
                            )
                            : isBooleanDataType(fieldDataType)
                              ? renderBooleanIcon(currentValue)
                              : readOnlyValue}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              </>
              )}
            </div>

            <div className="shrink-0 flex items-center gap-2 border-t border-[var(--neutral-gray-40)] px-5 py-1.5">
              {detailEditing && (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => void handleDeleteFromDetail()}
                  disabled={detailBusy || !detailRowId || incidentDetailUnavailable}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </Button>
              )}

              <div className="ml-auto flex items-center justify-end gap-2">
                {isInrepProgram && <Button id="incident-detail-download-pdf" type="button" variant="outline" size="sm" disabled={incidentDetailDisabled} onClick={() => { void downloadIncidentReportPdf({ ...detailFormValues, id: String(detailRowId) }, incidentDetailClients, labelsByField).catch(() => toast.error('The incident PDF could not be created.')); }}><Download className="h-4 w-4" />Download PDF</Button>}
                {isProgressReportProgram && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setProgressReportPreviewValues(detailFormValues);
                      setProgressReportPreviewOpen(true);
                    }}
                  >
                    Preview / Download
                  </Button>
                )}
                {detailEditing && (
                  <Button
                    type="button"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => void handleSaveDetail()}
                    disabled={detailBusy || incidentDetailUnavailable}
                  >
                    <Save className="h-4 w-4" />
                    Save
                  </Button>
                )}
                {isOdrProgram && !detailEditing && generalWritableFields.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => setDetailEditing(true)}
                    disabled={detailBusy}
                  >
                    <Pencil className="h-4 w-4" />
                    Edit
                  </Button>
                )}
                <Button type="button" variant="outline" size="sm" onClick={clearDetailState} disabled={detailBusy}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {isOdrProgram && courtChecklistOpen && (
        <OdrCourtChecklistDialog
          onClose={() => setCourtChecklistOpen(false)}
          loadCandidates={loadOdrCourtCandidates}
        />
      )}
      {isOdrProgram && selectedOdrTask && taskCandidateId > 0 && (
        <OdrCandidateTaskDialog
          candidateId={taskCandidateId}
          task={selectedOdrTask}
          open
          onOpenChange={(open) => {
            if (!open) {
              clearOdrTaskRoute();
            }
          }}
          onChanged={async () => {
            await refetch();
          }}
          onCompleted={async () => {
            clearOdrTaskRoute();
            await refetch();
          }}
        />
      )}
      {isOdrProgram && detailOdrTask && detailRowId > 0 && (
        <OdrCandidateTaskDialog
          candidateId={detailRowId}
          task={detailOdrTask}
          open
          onOpenChange={(open) => {
            if (!open) {
              setDetailOdrTask(null);
            }
          }}
          onChanged={refreshActiveDetailRow}
          onCompleted={async () => {
            clearDetailState();
            await refetch();
          }}
        />
      )}
      <ProgressReportPreview
        open={progressReportPreviewOpen}
        values={progressReportPreviewValues}
        onOpenChange={setProgressReportPreviewOpen}
      />
    </div>
  );
};
